import { useEffect, useState, useRef } from 'react';
import ProductionOrderDocument from '@/components/ProductionOrderDocument';
import SearchableSelect from '@/components/ui/SearchableSelect';
import OrderQueueList from '@/components/OrderQueueList';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { AlertCircle, Play, Pause, CheckCircle, Clock, Camera, FileText, FileImage, ZoomIn, ZoomOut, RotateCcw, Move, Upload, Search, ChevronLeft, ChevronRight, Eye, FileDown, Scale, Plus, Cog, X, Lock } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useQueryClient } from '@tanstack/react-query';
import { useTimeAlerts } from '../../hooks/useTimeAlerts';
import { useAuth } from '../../hooks/useAuth';
import { useNarrowViewport } from '../../hooks/useNarrowViewport';
import { getMexicoDateString, getMexicoDateTimeLocalString } from '../../lib/mexicoDate';

export interface ExecutionOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  productName: string;
  productCode?: string;
  lotCode?: string;
  machineName?: string;
  equipmentId?: number | null;
  quantityPlanned?: number;
  unit?: string;
  status: string;
  actualStartDate?: string | null;
  manufacturingRoutes: ManufacturingRoute[];
  designUrl?: string;
  notes?: string;
}

interface ManufacturingRoute {
  id: string;
  sequence: number;
  process: string;
  machineName?: string;
  equipmentId?: number | null;
  recordType?: string;
  status: 'pending' | 'in_progress' | 'completed';
  startedAt?: string;
  completedAt?: string;
  enablesChecklist?: boolean;
  isVerificationOnly?: boolean;
  solicitaBobinas?: boolean;
  registraTiempoCurado?: boolean;
}

const WEIGHT_UNIT_TO_KG: Record<string, number> = { kg: 1, lb: 0.453592, g: 0.001, oz: 0.0283495 };

// Weight registrations are logged as free-text bitácora events (no dedicated weight table yet).
// The weight amount is parsed from description ("Peso registrado: Y kg"); the concept/subconcept
// is parsed from evidenceText ("Concepto: X | Peso: Y kg") instead, since description encodes it
// as a "[X]" prefix with no "Concepto:" label.
//
// ROT-192: weight events classify into 3 buckets by their concept's top-level parent name —
// "Merma" (this tenant calls it "Producto en merma") for scrap, "Terminado" (this tenant calls it
// "Producto terminado") for confirmed finished goods, else "Proceso" (e.g. "Producto en Proceso").
// Both Merma and Terminado are REAL captured concepts operators explicitly choose — not derived —
// confirmed against production events (e.g. "Producto terminado > COMPLETO" logged directly on
// Corte/Curado/Laminación/Empaque, not only at the last step). No catalog flag needed: the
// catálogo de Registros de Peso already models every reason as a child of one of these parents.
type MermaBucket = 'proceso' | 'merma' | 'terminado';
function classifyConceptLabel(fullConceptLabel: string): MermaBucket {
  const topLevel = fullConceptLabel.split('>')[0]?.trim().toLowerCase() ?? '';
  if (topLevel.includes('merma')) return 'merma';
  if (topLevel.includes('terminado')) return 'terminado';
  return 'proceso';
}

function computeRouteWeightBreakdown(
  route: Pick<ManufacturingRoute, 'process'>,
  orderEvents: Array<{ processName?: string; description?: string; evidenceText?: string }>
): {
  procesoKg: number;
  mermaKg: number;
  productoTerminadoKg: number;
  totalBobinas: number;
  concepts: Array<{ concept: string; kg: number }>;
  mermaConcepts: Array<{ concept: string; kg: number }>;
} | null {
  const events = orderEvents.filter(
    ev => ev.processName === route.process && /peso registrado/i.test(ev.description ?? '')
  );
  if (events.length === 0) return null;

  const byConceptKg: Record<string, number> = {};
  const byMermaConceptKg: Record<string, number> = {};
  let procesoKg = 0;
  let mermaKg = 0;
  let productoTerminadoKg = 0;
  let totalBobinas = 0;
  for (const ev of events) {
    const desc = ev.description ?? '';
    // Concept lives in evidenceText ("Concepto: X | Peso: Y kg"), not description
    // (which encodes it as "[X] Peso registrado: Y kg" instead — no "Concepto:" label).
    const conceptMatch = (ev.evidenceText ?? '').match(/Concepto:\s*([^|]+)/i);
    // ROT-217: leading "-" allowed so an operator can correct a wrong entry by
    // logging the same value negative — it must net out against the original
    // via the += below, not be dropped by an unsigned-only capture group.
    const weightMatch = desc.match(/Peso registrado(?:\s+manual)?:\s*(-?[\d.]+)\s*(kg|lb|g|oz)/i);
    if (!weightMatch) continue;
    const kg = parseFloat(weightMatch[1]) * (WEIGHT_UNIT_TO_KG[weightMatch[2].toLowerCase()] ?? 1);
    const concept = conceptMatch ? conceptMatch[1].trim() : '(Sin concepto)';
    const bucket = classifyConceptLabel(concept);

    if (bucket === 'merma') {
      // Strip the "Merma > " prefix for display — the grouping is already implied by section.
      const reason = concept.includes('>') ? concept.split('>').pop()!.trim() : concept;
      byMermaConceptKg[reason] = (byMermaConceptKg[reason] ?? 0) + kg;
      mermaKg += kg;
    } else if (bucket === 'terminado') {
      productoTerminadoKg += kg;
    } else {
      byConceptKg[concept] = (byConceptKg[concept] ?? 0) + kg;
      procesoKg += kg;
    }
    // ROT-204: bobinas is folded into the same free-text bitácora as the
    // weight ("... - Bobinas: N ...") — sum it per process alongside kg.
    // ROT-226: N may be negative (same correction mechanism as kg, ROT-217).
    const bobinasMatch = desc.match(/Bobinas:\s*(-?\d+)/i);
    if (bobinasMatch) totalBobinas += parseInt(bobinasMatch[1], 10);
  }

  const concepts = Object.entries(byConceptKg)
    .sort((a, b) => b[1] - a[1])
    .map(([concept, kg]) => ({ concept, kg }));
  const mermaConcepts = Object.entries(byMermaConceptKg)
    .sort((a, b) => b[1] - a[1])
    .map(([concept, kg]) => ({ concept, kg }));

  return { procesoKg, mermaKg, productoTerminadoKg, totalBobinas, concepts, mermaConcepts };
}

interface AssignedMachine {
  id: string;
  machineName: string;
  machineCode?: string;
  priority: number;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  startedAt?: string;
  completedAt?: string;
}

export default function PlantExecution() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManualWeight = user?.permissions?.includes('plant:manual_weight') ?? false;
  const canCompleteRoute = user?.permissions?.includes('plant:complete_route') ?? false;
  const canAssignMachine = (user?.permissions?.includes('orders:update') || user?.permissions?.includes('plant:assign_machine')) ?? false;

  const [orders, setOrders] = useState<ExecutionOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<ExecutionOrder | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<ManufacturingRoute | null>(null);
  const [recordData, setRecordData] = useState('');
  const [verificationChecked, setVerificationChecked] = useState(false);
  const [verificationObservations, setVerificationObservations] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isNarrow = useNarrowViewport(1024);
  const effectiveCollapsed = isNarrow || isCollapsed;
  const [showQueueDrawer, setShowQueueDrawer] = useState(false);

  useEffect(() => {
    if (!isNarrow) setShowQueueDrawer(false);
  }, [isNarrow]);

  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [detailOrder, setDetailOrder] = useState<any>(null);

  const [routeEvidenceMap, setRouteEvidenceMap] = useState<Record<string, any>>({});
  
  // Design image modal state
  const [showDesignImageModal, setShowDesignImageModal] = useState(false);
  const [designModalOrder, setDesignModalOrder] = useState<ExecutionOrder | null>(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [designMeta, setDesignMeta] = useState<{ version: number; comments: string | null; imageDate: string | null; uploaderName: string | null; uploaderUsername: string | null; uploadedAt: string } | null>(null);
  
  // Pause modal state
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseObservations, setPauseObservations] = useState('');
  const [pauseOperator, setPauseOperator] = useState('');
  const [routeToPause, setRouteToPause] = useState<ManufacturingRoute | null>(null);
  const [catalogPauseReasonsList, setCatalogPauseReasonsList] = useState<Array<{id: number; code: string; name: string}>>([]);
  const [catalogEmployees, setCatalogEmployees] = useState<Array<{id: number; numEmpleado: number | null; name: string; puesto?: string}>>([]);
  const [selectedPauseReasonId, setSelectedPauseReasonId] = useState<string>('');

  // Checklist Modal state
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistFormData, setChecklistFormData] = useState<any>({});
  const [routeForChecklist, setRouteForChecklist] = useState<ManufacturingRoute | null>(null);
  const [isEditingChecklist, setIsEditingChecklist] = useState(false);
  const [checklistHistoryBanner, setChecklistHistoryBanner] = useState<string | null>(null);

  // Events per order (loaded when order is selected, refreshed after each event)
  const [orderEvents, setOrderEvents] = useState<any[]>([]);

  // Real-time presence — who else is viewing this order
  const [presenceOthers, setPresenceOthers] = useState<string[]>([]);
  const presenceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Bitácora collapse toggle (collapsed by default)
  const [showBitacora, setShowBitacora] = useState(false);
  const [showProcessTimeDetails, setShowProcessTimeDetails] = useState(false);

  // Operator registration per route
  const [operatorInputByRoute, setOperatorInputByRoute] = useState<Record<string, string>>({});
  const [showOperatorFor, setShowOperatorFor] = useState<string | null>(null);

  // Operator field in weight modal
  const [weightOperator, setWeightOperator] = useState('');

  // Time alerts
  const { data: timeAlertsData } = useTimeAlerts();
  const [liveElapsedMinutes, setLiveElapsedMinutes] = useState<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const selectedOrderAlert = (timeAlertsData?.alerts || []).find(a => String(a.orderId) === String(selectedOrder?.id)) ?? null;
  const selectedProcessAlerts = (timeAlertsData?.processAlerts || []).filter(a => String(a.orderId) === String(selectedOrder?.id));

  // Live elapsed-time ticker — updates every 30 seconds from actualStartDate
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!selectedOrderAlert?.actualStartDate) return;
    const calc = () => {
      const start = new Date(selectedOrderAlert.actualStartDate).getTime();
      setLiveElapsedMinutes(Math.floor((Date.now() - start) / 60000));
    };
    calc();
    timerRef.current = setInterval(calc, 30000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [selectedOrderAlert?.actualStartDate]);

  // Load all events for the selected order (used for operators, previous weighings, previous pauses)
  const loadOrderEvents = async (orderId: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/production-order-events/order/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrderEvents(data);
      }
    } catch {
      // non-critical
    }
  };

  // ─── Presence ─────────────────────────────────────────────────────────────
  const registerPresence = async (orderId: string) => {
    const token = localStorage.getItem('token');
    await fetch(`/api/production-orders/${orderId}/presence`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  };

  const unregisterPresence = async (orderId: string) => {
    const token = localStorage.getItem('token');
    await fetch(`/api/production-orders/${orderId}/presence`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  };

  const pollPresence = async (orderId: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/production-orders/${orderId}/presence`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const { others } = await res.json();
        setPresenceOthers(others ?? []);
      }
    } catch {}
  };

  const startPresence = (orderId: string) => {
    if (presenceIntervalRef.current) clearInterval(presenceIntervalRef.current);
    registerPresence(orderId);
    pollPresence(orderId);
    presenceIntervalRef.current = setInterval(() => {
      registerPresence(orderId);
      pollPresence(orderId);
    }, 20_000);
  };

  const stopPresence = (orderId: string) => {
    if (presenceIntervalRef.current) { clearInterval(presenceIntervalRef.current); presenceIntervalRef.current = null; }
    setPresenceOthers([]);
    unregisterPresence(orderId);
  };

  // Returns pause duration as a formatted string. Looks for the next "Inicio de proceso" event
  // for the same process after the pause, to measure how long the pause lasted.
  const getPauseDuration = (pauseEvent: any, events: any[]): string => {
    const sameProcessStarts = events
      .filter(e =>
        e.processName === pauseEvent.processName &&
        e.description.startsWith('Inicio de proceso') &&
        new Date(e.occurredAt).getTime() > new Date(pauseEvent.occurredAt).getTime()
      )
      .sort((a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime());

    if (sameProcessStarts.length === 0) return 'En curso';
    const ms = new Date(sameProcessStarts[0].occurredAt).getTime() - new Date(pauseEvent.occurredAt).getTime();
    const mins = Math.floor(ms / 60000);
    const hrs = Math.floor(mins / 60);
    return hrs > 0 ? `${hrs}h ${mins % 60}m` : `${mins}m`;
  };

  // ROT-201: elapsed time between a route's startedAt and completedAt (or now,
  // if still in progress) — used to show how long a verification-only step
  // (e.g. Curado) has been/was running.
  const formatElapsedDuration = (startIso: string, endIso?: string): string => {
    const ms = new Date(endIso || Date.now()).getTime() - new Date(startIso).getTime();
    const mins = Math.max(0, Math.floor(ms / 60000));
    const hrs = Math.floor(mins / 60);
    return hrs > 0 ? `${hrs}h ${mins % 60}m` : `${mins}m`;
  };

  // ROT-207: general (not per-process) plant-floor note, persisted on the
  // order itself (notes.plantExecutionObservations) — unlike the per-action
  // observation fields below, which are folded into one-off event text.
  const [plantExecutionObservations, setPlantExecutionObservations] = useState('');
  const [savingPlantObservations, setSavingPlantObservations] = useState(false);

  // Weight registration modal state
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [weightValue, setWeightValue] = useState('');
  const [weightUnit, setWeightUnit] = useState('kg');
  const [weightMachineName, setWeightMachineName] = useState('');
  const [weightObservations, setWeightObservations] = useState('');
  const [bobinasCount, setBobinasCount] = useState(''); // ROT-201: shown alongside weight when route.solicitaBobinas
  const [routeForWeight, setRouteForWeight] = useState<ManufacturingRoute | null>(null);
  const [isRecordingWeight, setIsRecordingWeight] = useState(false);
  const [scaleImage, setScaleImage] = useState<string>('');
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [analysisConfidence, setAnalysisConfidence] = useState<'high' | 'low' | null>(null);
  const [weightConcept, setWeightConcept] = useState('');
  const [weightSubConcept, setWeightSubConcept] = useState('');
  const [availableWeightRecords, setAvailableWeightRecords] = useState<Array<{id: number; code: string; name: string}>>([]);
  const [weightSubConcepts, setWeightSubConcepts] = useState<Array<{id: number; code: string; name: string}>>([]);
  const [isLoadingSubConcepts, setIsLoadingSubConcepts] = useState(false);

  // Parallel execution mode - allows starting any step without waiting for previous ones
  const [parallelExecutionMode, setParallelExecutionMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('plantExecutionParallelMode');
    return saved === 'true';
  });

  const toggleParallelMode = () => {
    const newValue = !parallelExecutionMode;
    setParallelExecutionMode(newValue);
    localStorage.setItem('plantExecutionParallelMode', String(newValue));
  };

  // Multi-machine support states
  const [assignedMachines, setAssignedMachines] = useState<AssignedMachine[]>([]);
  const [showMachineModal, setShowMachineModal] = useState(false);
  const [newMachineName, setNewMachineName] = useState('');
  const [availableEquipment, setAvailableEquipment] = useState<any[]>([]);

  // Equipment identity migration Phase 3: matches a raw machine-name string
  // (the order's or route's already-assigned machineName) against the
  // equipment catalog already loaded for this order, mirroring
  // matchEquipmentId's trim+lowercase, code-or-name semantics. This only
  // echoes which machine an event happened on — it does not change
  // machineName/equipmentName, exactly like Phase 1's other event-creation
  // call sites.
  const resolveEquipmentIdFromCatalog = (machineName: string | null | undefined): number | null => {
    if (!machineName) return null;
    const trimmed = machineName.trim().toLowerCase();
    const match = availableEquipment.find(
      (eq: any) => eq.code?.toLowerCase() === trimmed || eq.name?.toLowerCase() === trimmed
    );
    return match?.id ?? null;
  };

  // Equipment identity migration Phase 5: prefer the order's/route's own
  // already-resolved equipmentId (returned by the backend) over re-matching
  // its machineName text against the catalog. Falls back to
  // resolveEquipmentIdFromCatalog only for legacy/orphaned data with no
  // stored equipmentId.
  const resolveOrderEquipmentId = (
    entity: { equipmentId?: number | null; machineName?: string | null } | null | undefined
  ): number | null => {
    if (!entity) return null;
    if (entity.equipmentId != null) return entity.equipmentId;
    return resolveEquipmentIdFromCatalog(entity.machineName);
  };

  // Load available equipment from catalogs
  const loadAvailableEquipment = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/catalogs/equipment', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableEquipment(data.filter((eq: any) => eq.isActive));
      }
    } catch (error) {
      console.error('Error loading equipment:', error);
    }
  };

  // Load pause reasons from catalog
  const loadPauseReasons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/catalogs/pause-reasons', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCatalogPauseReasonsList(data.filter((r: any) => r.isActive));
      }
    } catch (error) {
      console.error('Error loading pause reasons:', error);
    }
  };

  // Load employees from catalog
  const loadCatalogEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/catalogs/employees', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCatalogEmployees(data.filter((e: any) => e.isActive !== false));
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  // Load available weight record concepts from catalogs
  const loadWeightRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/catalogs/weight-records', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableWeightRecords(data.filter((wr: any) => wr.isActive));
      }
    } catch (error) {
      console.error('Error loading weight records:', error);
    }
  };

  // Load sub-concepts for a selected parent weight record
  const loadWeightSubConcepts = async (parentId: number) => {
    setIsLoadingSubConcepts(true);
    setWeightSubConcepts([]);
    setWeightSubConcept('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/catalogs/weight-records/${parentId}/sub-concepts`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setWeightSubConcepts(data.filter((sc: any) => sc.isActive));
      }
    } catch (error) {
      console.error('Error loading weight sub-concepts:', error);
    } finally {
      setIsLoadingSubConcepts(false);
    }
  };

  // Load machines assigned to order
  const loadAssignedMachines = async (orderId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/machines`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAssignedMachines(data);
      }
    } catch (error) {
      console.error('Error loading assigned machines:', error);
    }
  };

  // Add machine to order
  const addMachineToOrder = async () => {
    if (!selectedOrder || !newMachineName.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${selectedOrder.id}/machines`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          machineName: newMachineName,
          equipmentId: resolveEquipmentIdFromCatalog(newMachineName),
          priority: assignedMachines.length + 1,
        }),
      });
      if (response.ok) {
        await loadAssignedMachines(selectedOrder.id);
        setNewMachineName('');
        setShowMachineModal(false);
      } else {
        const error = await response.json();
        alert(error.error || 'Error al agregar máquina');
      }
    } catch (error) {
      console.error('Error adding machine:', error);
      alert('Error al agregar máquina');
    }
  };

  // Remove machine from order
  const removeMachineFromOrder = async (machineId: string) => {
    if (!selectedOrder) return;
    if (!confirm('¿Está seguro de remover esta máquina de la orden?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${selectedOrder.id}/machines/${machineId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        await loadAssignedMachines(selectedOrder.id);
      }
    } catch (error) {
      console.error('Error removing machine:', error);
    }
  };

  // Load orders assigned to machines
  useEffect(() => {
    loadOrders();
    loadPauseReasons();
    loadCatalogEmployees();
  }, []);

  const loadOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = getMexicoDateString(); // YYYY-MM-DD en hora de México, no UTC

      // Fetch both: authorized orders scheduled for today (Hoy column), and authorized orders already in production (actualStartDate set)
      const [resToday, resInProd] = await Promise.all([
        fetch(`/api/production-orders?status=AUTHORIZED&plannedDate=${today}&limit=200`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`/api/production-orders?status=AUTHORIZED&inProduction=true&limit=200`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      const todayData = resToday.ok ? await resToday.json() : { data: [] };
      const inProdData = resInProd.ok ? await resInProd.json() : { data: [] };

      const todayList = Array.isArray(todayData.data) ? todayData.data : (Array.isArray(todayData) ? todayData : []);
      const inProdList = Array.isArray(inProdData.data) ? inProdData.data : (Array.isArray(inProdData) ? inProdData : []);

      // Merge, deduplicate by id
      const seen = new Set<string>();
      const merged = [...todayList, ...inProdList].filter((order: any) => {
        if (seen.has(String(order.id))) return false;
        seen.add(String(order.id));
        return true;
      });

      const formattedOrders = merged.map((order: any) => ({
        id: order.id,
        orderNumber: order.orderNumber,
        customerName: order.customerName,
        productName: order.productName,
        productCode: order.productCode || '',
        lotCode: order.lotCode || '',
        machineName: order.machineName,
        equipmentId: order.equipmentId ?? null,
        quantityPlanned: Number(order.quantityPlanned) || 0,
        unit: order.unit || 'kg',
        status: order.status || 'CREATED',
        actualStartDate: order.actualStartDate || null,
        manufacturingRoutes: [],
        designUrl: order.designUrl,
        notes: order.notes,
      }));

      setOrders(formattedOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadOrderRoutes = async (orderId: string, order: ExecutionOrder) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/production-orders/${orderId}/manufacturing-routes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const routes = await response.json();
        const formattedRoutes = routes.map((route: any) => ({
          ...route,
          status: route.status || 'pending',
          equipmentId: route.equipmentId ?? null,
        }));
        
        // Update selected order first with routes
        const updatedOrder = { ...order, manufacturingRoutes: formattedRoutes };
        setSelectedOrder(updatedOrder);
        
        // Load evidence for each route in parallel
        const evidencePromises = formattedRoutes.map(async (route: any) => {
          try {
            const evidenceRes = await fetch(`/api/production-orders/${orderId}/manufacturing-routes/${route.id}/evidence`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            if (evidenceRes.ok) {
              const evidences = await evidenceRes.json();
              if (evidences.length > 0) {
                return { routeId: route.id, evidence: evidences[evidences.length - 1] };
              }
            }
          } catch (err) {
            console.error('Error loading evidence for route:', err);
          }
          return null;
        });
        
        const results = await Promise.all(evidencePromises);
        const newEvidenceMap: Record<string, any> = {};
        results.forEach(result => {
          if (result) {
            newEvidenceMap[result.routeId] = result.evidence;
          }
        });
        setRouteEvidenceMap(newEvidenceMap);

        // Update selected order with routes — status comes straight from the DB,
        // it is not inferred from evidence presence. A route can have evidence
        // left behind by a failed completion attempt and still be genuinely
        // pending; only the DB's own status column is trustworthy here.
        const updatedOrderFinal = { ...order, manufacturingRoutes: formattedRoutes };
        setSelectedOrder(updatedOrderFinal);

        // Load events for this order (operators, pauses, weighings history)
        loadOrderEvents(orderId);

        // Check if ALL routes are genuinely completed in the DB (order is done)
        const allRoutesCompleted = formattedRoutes.length > 0 && formattedRoutes.every((route: any) => route.status === 'completed');
        if (allRoutesCompleted) {
          // Mark order as completed
          try {
            const completeRes = await fetch(`/api/production-orders/${orderId}/status`, {
              method: 'PATCH',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                status: 'COMPLETED',
              }),
            });
            if (completeRes.ok) {
              // Remove from orders list
              setOrders(prev => prev.filter(o => o.id !== orderId));
              setSelectedOrder(null);
              setSelectedRoute(null);
            }
          } catch (err) {
            console.error('Error marking order as completed:', err);
          }
        }
      }
    } catch (error) {
      console.error('Error loading routes:', error);
    }
  };

  const startRoute = async (route: ManufacturingRoute) => {
    if (!selectedOrder) return;
    
    // Check if this is the first process to be started
    const isFirstProcess = !selectedOrder.manufacturingRoutes.some(r => r.status === 'in_progress' || r.status === 'completed');
    
    if (route.enablesChecklist) {
        try {
          const token = localStorage.getItem('token');
          const checkRes = await fetch(`/api/production-orders/${selectedOrder.id}/checklist`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          const now = getMexicoDateTimeLocalString();
          const productCode = selectedOrder.productCode || '';
          const baseData = {
            cliente: selectedOrder.customerName,
            fecha: getMexicoDateString(),
            ordenProduccion: selectedOrder.orderNumber,
            lote: selectedOrder.lotCode || '',
            equipo: selectedOrder.machineName || route.machineName || '',
            velocidad: '',
            tempTunel: '',
            tempTambor: '',
            tempTintero: '',
            tensionEmbobinador: '',
            tensionDesbobinador: '',
            cuerpos: Array(8).fill(null).map(() => ({ anilox: '', codigoAnilox: '', bcm: '', color: '', stickiback: '', viscosidad: '', aprobado: false })),
            operador: '',
            supervisor: '',
            operadorArranque: '',
            supervisorProduccion: '',
            aseguramientoCalidad: '',
            fechaHoraCambio: now,
            fechaHoraEntonacion: now,
            fechaHoraArranque: now,
          };

          if (!checkRes.ok) {
            // No checklist at all — show modal pre-filled with history
            setRouteForChecklist(route);
            setChecklistHistoryBanner(null);
            const filledData = await applyChecklistHistory(baseData, selectedOrder.id, productCode);
            setChecklistFormData(filledData);
            setShowChecklistModal(true);
            return;
          }

          // Checklist record exists — check if it has technical data
          const checkData = await checkRes.json();
          const existingChecklist = checkData.checklistData || {};
          if (isChecklistTechnicallyEmpty(existingChecklist)) {
            // Exists but empty — show modal pre-filled with history so operator can review
            setRouteForChecklist(route);
            setChecklistHistoryBanner(null);
            const base = { ...baseData, ...existingChecklist };
            const filledData = await applyChecklistHistory(base, selectedOrder.id, productCode);
            setChecklistFormData(filledData);
            setShowChecklistModal(true);
            return;
          }
          // Checklist already has data — proceed normally (no modal needed)
        } catch (error) {
           console.error("Error checking checklist", error);
        }
    }

    try {
      const token = localStorage.getItem('token');

      // If this is the first process and the order hasn't been started yet,
      // automatically mark it as started (sets actualStartDate → moves to IN_PRODUCTION)
      if (isFirstProcess && !selectedOrder.actualStartDate) {
        try {
          const startRes = await fetch(`/api/production-orders/${selectedOrder.id}/mark-started`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (startRes.ok) {
            const startData = await startRes.json();
            // Update local state: actualStartDate + status → IN_PRODUCTION
            setSelectedOrder(prev => prev ? {
              ...prev,
              actualStartDate: startData.actualStartDate,
            } : prev);
            setOrders(prev => prev.map(o =>
              o.id === selectedOrder.id
                ? { ...o, actualStartDate: startData.actualStartDate }
                : o
            ));
          }
        } catch (startErr) {
          console.error('Error marking order as started:', startErr);
        }
      }
      
      // Send event to Visor 360 RT
      await fetch('/api/production-order-events', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productionOrderId: selectedOrder.id,
          orderNumber: selectedOrder.orderNumber,
          equipmentName: selectedOrder.machineName || route.machineName || null,
          equipmentId: resolveOrderEquipmentId(route?.equipmentId != null ? route : selectedOrder),
          processName: route.process,
          description: `Inicio de proceso: ${route.process} - Orden ${selectedOrder.orderNumber}`,
          evidenceType: 'TEXT',
          evidenceText: `/app/production-orders?view=${selectedOrder.id}`,
        }),
      });
      
      // Set route to in_progress and immediately show the form
      setSelectedRoute({ ...route, status: 'in_progress', startedAt: new Date().toISOString() });
      setRecordData(''); // Clear previous data
      setVerificationChecked(false);
      setVerificationObservations('');
    } catch (error) {
      console.error('Error starting route:', error);
    }
  };

  // Returns true when all "template" fields (velocidad, temps, cuerpos) are blank.
  // Used to decide whether to auto-apply historical data even when a checklist record exists.
  const isChecklistTechnicallyEmpty = (data: any): boolean => {
    if (!data) return true;
    const hasVelocidad = !!(data.velocidad || '').trim();
    const hasTemps = !!(data.tempTunel || data.tempTambor || data.tempTintero);
    const hasCuerpos = Array.isArray(data.cuerpos) &&
      data.cuerpos.some((c: any) => (c.anilox || c.color || c.bcm || '').trim() !== '');
    return !hasVelocidad && !hasTemps && !hasCuerpos;
  };

  // Move a cuerpo column left or right in the sequence
  const moveCuerpo = (fromIdx: number, direction: 'left' | 'right') => {
    const toIdx = direction === 'left' ? fromIdx - 1 : fromIdx + 1;
    if (toIdx < 0 || toIdx > 7) return;
    const newCuerpos = [...(checklistFormData.cuerpos || [])];
    [newCuerpos[fromIdx], newCuerpos[toIdx]] = [newCuerpos[toIdx], newCuerpos[fromIdx]];
    setChecklistFormData({ ...checklistFormData, cuerpos: newCuerpos });
  };

  const applyChecklistHistory = async (baseData: any, orderId: string, productCode: string) => {
    if (!productCode) return baseData;
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ productCode, excludeOrderId: orderId });
      const res = await fetch(`/api/production-orders/checklist-history?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) return baseData;
      const { orderNumber, checklistData: hist } = await res.json();
      if (!hist) return baseData;
      // Merge only template-level fields (NOT order-specific: cliente, fecha, ordenProduccion, lote, equipo, operador, supervisor)
      const merged = {
        ...baseData,
        velocidad:            hist.velocidad            || baseData.velocidad,
        tempTunel:            hist.tempTunel            || baseData.tempTunel,
        tempTambor:           hist.tempTambor           || baseData.tempTambor,
        tempTintero:          hist.tempTintero          || baseData.tempTintero,
        tensionEmbobinador:   hist.tensionEmbobinador   || baseData.tensionEmbobinador,
        tensionDesbobinador:  hist.tensionDesbobinador  || baseData.tensionDesbobinador,
        // Copy template data from history but always reset approval checkboxes
        cuerpos: hist.cuerpos?.length
          ? hist.cuerpos.map((c: any) => ({ ...c, aprobado: false }))
          : baseData.cuerpos,
      };
      setChecklistHistoryBanner(orderNumber);
      return merged;
    } catch (_) {
      return baseData;
    }
  };

  const saveChecklist = async () => {
    if (!selectedOrder) return;
    if (!isEditingChecklist && !routeForChecklist) return;

    try {
      const token = localStorage.getItem('token');
      const method = isEditingChecklist ? 'PUT' : 'POST';
      const response = await fetch(`/api/production-orders/${selectedOrder.id}/checklist`, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          checklistData: checklistFormData,
        }),
      });

      if (!response.ok) {
        throw new Error('Error saving checklist');
      }

      setShowChecklistModal(false);
      setIsEditingChecklist(false);

      if (!isEditingChecklist && routeForChecklist) {
        // Proceed to start the route now that checklist is saved
        const routeToStart = routeForChecklist;
        setRouteForChecklist(null);
        await startRoute(routeToStart);
      } else {
        setRouteForChecklist(null);
      }
      
    } catch (error) {
      console.error('Error saving checklist:', error);
      alert('Error al guardar el CheckList');
    }
  };

  const openChecklistForEdit = async () => {
    if (!selectedOrder) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/production-orders/${selectedOrder.id}/checklist`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setChecklistHistoryBanner(null);
      const now = getMexicoDateTimeLocalString();
      const productCode = selectedOrder.productCode || '';

      const buildBaseData = () => ({
        cliente: selectedOrder.customerName,
        fecha: getMexicoDateString(),
        ordenProduccion: selectedOrder.orderNumber,
        lote: selectedOrder.lotCode || '',
        equipo: selectedOrder.machineName || '',
        velocidad: '',
        tempTunel: '',
        tempTambor: '',
        tempTintero: '',
        tensionEmbobinador: '',
        tensionDesbobinador: '',
        cuerpos: Array(8).fill(null).map(() => ({ anilox: '', codigoAnilox: '', bcm: '', color: '', stickiback: '', viscosidad: '', aprobado: false })),
        operador: '',
        supervisor: '',
        operadorArranque: '',
        supervisorProduccion: '',
        aseguramientoCalidad: '',
        fechaHoraCambio: now,
        fechaHoraEntonacion: now,
        fechaHoraArranque: now,
      });

      if (res.ok) {
        const data = await res.json();
        const loaded = data.checklistData || {};
        const withTimestamps = {
          ...loaded,
          fechaHoraCambio:     loaded.fechaHoraCambio     || now,
          fechaHoraEntonacion: loaded.fechaHoraEntonacion || now,
          fechaHoraArranque:   loaded.fechaHoraArranque   || now,
        };

        // If the saved checklist has no technical data (velocidad/temps/cuerpos all blank),
        // auto-apply history so the operator gets the pre-fill from the last good job.
        if (isChecklistTechnicallyEmpty(loaded)) {
          const base = { ...buildBaseData(), ...withTimestamps };
          const filledData = await applyChecklistHistory(base, selectedOrder.id, productCode);
          setChecklistFormData(filledData);
        } else {
          setChecklistFormData(withTimestamps);
        }
      } else {
        // No checklist record at all — pre-fill from defaults + historical data
        const filledData = await applyChecklistHistory(buildBaseData(), selectedOrder.id, productCode);
        setChecklistFormData(filledData);
      }
      setIsEditingChecklist(true);
      setRouteForChecklist(null);
      setShowChecklistModal(true);
    } catch (error) {
      console.error('Error loading checklist:', error);
    }
  };

  const assignRouteMachine = async (route: ManufacturingRoute, machineName: string) => {
    if (!selectedOrder) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/production-orders/${selectedOrder.id}/manufacturing-routes/${route.id}/machine`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ machineName: machineName || null }),
      });
      if (!res.ok) return;
      const updated = await res.json();
      setSelectedOrder(prev => prev ? {
        ...prev,
        manufacturingRoutes: prev.manufacturingRoutes.map(r =>
          r.id === route.id ? { ...r, machineName: updated.machineName, equipmentId: updated.equipmentId } : r
        ),
      } : prev);
      setOrders(prev => prev.map(o =>
        o.id === selectedOrder.id
          ? { ...o, manufacturingRoutes: o.manufacturingRoutes.map(r => r.id === route.id ? { ...r, machineName: updated.machineName, equipmentId: updated.equipmentId } : r) }
          : o
      ));
      // ROT-218: keep the Kanban (Planning Board) in sync with this
      // reassignment. The Gantt view (GanttView.tsx) fetches its own data
      // via a plain fetch + local state (not react-query), and remounts
      // fresh every time the Planning Board switches into Gantt mode, so
      // there is no react-query cache key for it to invalidate here.
      queryClient.invalidateQueries({ queryKey: ['plannableOrders'] });
    } catch (error) {
      console.error('Error assigning machine to route:', error);
    }
  };

  const handlePauseClick = (route: ManufacturingRoute) => {
    setRouteToPause(route);
    setSelectedPauseReasonId('');
    setPauseObservations('');
    setPauseOperator('');
    setShowPauseModal(true);
  };

  const confirmPause = async () => {
    if (!selectedOrder || !routeToPause) return;
    if (!selectedPauseReasonId) {
      alert('Por favor selecciona un motivo de pausa');
      return;
    }
    if (!pauseOperator.trim()) {
      alert('Por favor ingresa el nombre del operador');
      return;
    }

    const selectedReason = catalogPauseReasonsList.find(r => String(r.id) === selectedPauseReasonId);
    const reasonLabel = selectedReason ? `${selectedReason.code} - ${selectedReason.name}` : '';
    const descriptionParts = [
      `Pausa en proceso: ${routeToPause.process} - Motivo: ${reasonLabel} - Operador: ${pauseOperator.trim()}`,
    ];
    if (pauseObservations.trim()) descriptionParts.push(`Observaciones: ${pauseObservations.trim()}`);

    try {
      const token = localStorage.getItem('token');
      
      // Send pause event to Visor 360 RT
      await fetch('/api/production-order-events', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productionOrderId: selectedOrder.id,
          orderNumber: selectedOrder.orderNumber,
          equipmentName: selectedOrder.machineName || routeToPause.machineName || null,
          equipmentId: resolveOrderEquipmentId(routeToPause?.equipmentId != null ? routeToPause : selectedOrder),
          processName: routeToPause.process,
          description: descriptionParts.join(' | '),
          evidenceType: 'TEXT',
          evidenceText: `/app/production-orders?view=${selectedOrder.id}`,
        }),
      });

      // Clear the route and form
      setSelectedRoute(null);
      setRecordData('');
      setVerificationChecked(false);
      setVerificationObservations('');
      setShowPauseModal(false);
      setPauseObservations('');
      setSelectedPauseReasonId('');
      setPauseOperator('');
      setRouteToPause(null);

      // Refresh events so pause history shows up immediately
      loadOrderEvents(selectedOrder.id);
    } catch (error) {
      console.error('Error pausing route:', error);
    }
  };

  const handleWeightClick = (route: ManufacturingRoute) => {
    if (selectedOrder?.status === 'CLOSED') {
      alert('Esta orden está cerrada. No se pueden registrar más pesos.');
      return;
    }
    if (route.status === 'completed') {
      alert('Este paso ya fue completado. No se pueden registrar más pesos en este paso.');
      return;
    }
    setRouteForWeight(route);
    setWeightValue('');
    setWeightUnit('kg');
    // ROT-223: default to the route's (or, failing that, the order's)
    // currently-planned machine — same precedence recordWeight used to
    // auto-derive, now just an editable starting point instead of a fixed
    // value, so the common single-machine case needs no extra clicks.
    setWeightMachineName(route.machineName || selectedOrder?.machineName || '');
    setWeightObservations('');
    setBobinasCount('');
    setWeightOperator('');
    setWeightConcept('');
    setWeightSubConcept('');
    setWeightSubConcepts([]);
    setScaleImage('');
    setAnalysisConfidence(null);
    setShowWeightModal(true);
  };

  const analyzeScaleImage = async (imageData: string) => {
    setIsAnalyzingImage(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/analyze-scale-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ imageData }),
      });

      if (!response.ok) {
        throw new Error('Error al analizar la imagen');
      }

      const result = await response.json();
      
      if (result.error) {
        if (result.error === 'no_display_detected') {
          alert('No se detectó una pantalla de báscula clara en la imagen. Por favor, toma otra foto.');
        } else if (result.error === 'budget_exceeded') {
          alert('Se agotó el presupuesto de IA. Por favor ingresa el peso manualmente.');
        } else {
          alert('Error al analizar la imagen. Por favor ingresa el peso manualmente.');
        }
        return;
      }

      if (result.weight !== undefined) {
        setWeightValue(result.weight.toString());
        if (result.unit) {
          const unitMap: Record<string, string> = { 'kg': 'kg', 'lb': 'lb', 'g': 'g', 'oz': 'oz', 'kilogramos': 'kg', 'libras': 'lb', 'gramos': 'g', 'onzas': 'oz' };
          const mappedUnit = unitMap[result.unit.toLowerCase()] || 'kg';
          setWeightUnit(mappedUnit);
        }
        setAnalysisConfidence(result.confidence || 'low');
        setScaleImage('');
      }
    } catch (error) {
      console.error('Error analyzing scale image:', error);
      alert('Error al analizar la imagen. Por favor ingresa el peso manualmente.');
    } finally {
      setIsAnalyzingImage(false);
    }
  };

  const recordWeight = async () => {
    if (!selectedOrder || !routeForWeight) return;
    if (!canManualWeight && !scaleImage) {
      alert('Debes fotografiar la báscula. Los operadores deben registrar el peso mediante fotografía.');
      return;
    }
    if (!weightValue.trim()) {
      alert('Por favor ingresa el peso');
      return;
    }
    if (!weightConcept) {
      alert('Por favor selecciona un concepto de peso');
      return;
    }
    if (!weightMachineName) {
      alert('Por favor selecciona la máquina');
      return;
    }
    if (weightSubConcepts.length > 0 && !weightSubConcept) {
      alert('Por favor selecciona un subconcepto de peso');
      return;
    }
    if (routeForWeight.solicitaBobinas && !bobinasCount.trim()) {
      alert('Por favor ingresa la cantidad de bobinas');
      return;
    }

    setIsRecordingWeight(true);
    try {
      const token = localStorage.getItem('token');

      const conceptLabel = weightSubConcept ? `${weightConcept} > ${weightSubConcept}` : weightConcept;
      const conceptText = conceptLabel ? `[${conceptLabel}] ` : '';
      const operatorText = weightOperator.trim() ? ` - Operador: ${weightOperator.trim()}` : '';
      const bobinasText = routeForWeight.solicitaBobinas ? ` - Bobinas: ${bobinasCount.trim()}` : '';
      const description = weightObservations.trim()
        ? `${conceptText}Peso registrado: ${weightValue} ${weightUnit}${bobinasText} - ${routeForWeight.process}${operatorText} | Obs: ${weightObservations}`
        : `${conceptText}Peso registrado: ${weightValue} ${weightUnit}${bobinasText} - ${routeForWeight.process}${operatorText}`;

      const response = await fetch('/api/production-order-events', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productionOrderId: selectedOrder.id,
          orderNumber: selectedOrder.orderNumber,
          equipmentName: weightMachineName || null,
          equipmentId: resolveEquipmentIdFromCatalog(weightMachineName),
          processName: routeForWeight.process,
          description: description,
          evidenceType: 'TEXT',
          evidenceText: `${conceptLabel ? `Concepto: ${conceptLabel} | ` : ''}Peso: ${weightValue} ${weightUnit}${routeForWeight.solicitaBobinas ? ` | Bobinas: ${bobinasCount.trim()}` : ''}${weightOperator.trim() ? ` | Operador: ${weightOperator.trim()}` : ''}${weightObservations ? ` | Observaciones: ${weightObservations}` : ''}`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 409) {
          alert((errorData as any).error || 'La orden está cerrada o el paso ya fue completado. No se puede registrar el peso.');
          setShowWeightModal(false);
          return;
        }
        console.error('Error response:', errorData);
        throw new Error('Error al registrar el peso en el servidor');
      }

      setShowWeightModal(false);
      setWeightValue('');
      setWeightMachineName('');
      setWeightObservations('');
      setBobinasCount('');
      setWeightOperator('');
      setWeightConcept('');
      setWeightSubConcept('');
      setWeightSubConcepts([]);
      setRouteForWeight(null);
      setScaleImage('');
      setAnalysisConfidence(null);
      if (selectedOrder) {
        loadOrderEvents(selectedOrder.id);
      }
      alert('Peso registrado exitosamente - Visible en Visor 360');
    } catch (error) {
      console.error('Error recording weight:', error);
      alert('Error al registrar el peso. Por favor intenta de nuevo.');
    } finally {
      setIsRecordingWeight(false);
    }
  };

  const completeRoute = async (route: ManufacturingRoute) => {
    if (!selectedOrder) return;

    // Verification-only steps (ROT-190) skip TEXT/IMAGE evidence capture
    // entirely — completion requires the confirmation checkbox instead.
    // effectiveRecordType makes every remaining route.recordType check
    // below treat a verification-only step exactly like a TEXTO step,
    // since the formatted verification string IS its evidence text.
    if (route.isVerificationOnly) {
      if (!verificationChecked) {
        alert('Por favor confirma que el proceso se realizó correctamente antes de completar');
        return;
      }
    } else if (!recordData.trim()) {
      alert('Por favor registra datos o sube una imagen antes de completar');
      return;
    }

    setIsUploading(true);
    const effectiveRecordType = route.isVerificationOnly ? 'TEXTO' : route.recordType;
    let evidenceValue = route.isVerificationOnly
      ? `Verificado por: ${user?.fullName || user?.email || 'Usuario'} — ${route.process} realizado correctamente.${verificationObservations.trim() ? ` Observaciones: ${verificationObservations.trim()}` : ''}`
      : recordData;

    try {
      const token = localStorage.getItem('token');

      // If it's an image, upload the raw file to Object Storage first — this only
      // stores the file and produces its path; it does not record any evidence or
      // completion yet, so it's safe to do before we know whether the server will
      // accept the completion.
      if (effectiveRecordType === 'IMAGEN' && recordData.startsWith('data:')) {
        try {
          // Convert base64 to blob properly
          const base64Data = recordData.split(',')[1];
          const mimeType = recordData.split(',')[0].match(/:(.*?);/)?.[1] || 'image/png';
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });

          // Get timestamp for unique filename
          const timestamp = Date.now();
          const extension = mimeType === 'image/jpeg' ? 'jpg' : 'png';
          const fileName = `${selectedOrder.orderNumber}_ruta${route.sequence}_${timestamp}.${extension}`;
          const filePath = `ordenesproduccion_piso/${selectedOrder.id}/${fileName}`;

          // Create File object with proper name
          const file = new File([blob], fileName, { type: mimeType });

          // Upload to Object Storage using fetch
          const formData = new FormData();
          formData.append('file', file);
          formData.append('path', filePath);

          console.log('Uploading image to:', filePath, 'size:', file.size);

          const uploadResponse = await fetch('/api/storage/upload', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            body: formData,
          });

          if (uploadResponse.ok) {
            const uploadData = await uploadResponse.json();
            evidenceValue = uploadData.path || filePath;
            console.log('Image uploaded successfully:', evidenceValue);
          } else {
            const errorText = await uploadResponse.text();
            console.error('Upload failed:', uploadResponse.status, errorText);
          }
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          alert('Error al subir la imagen, pero se registrará localmente');
        }
      }

      // Status and evidence are persisted together in one request - the
      // backend wraps both writes in a DB transaction, so a step can never
      // end up "completed" with its evidence silently missing (the residual
      // gap left after ROT-191's Task 8/9: two separate calls meant a failed
      // second call had nothing tying it back to the first).
      const evidencePayload = effectiveRecordType
        ? { type: effectiveRecordType === 'TEXTO' ? 'TEXT' : 'IMAGE', value: evidenceValue }
        : undefined;

      const statusRes = await fetch(
        `/api/production-orders/${selectedOrder.id}/manufacturing-routes/${route.id}/status`,
        {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'completed', evidence: evidencePayload }),
        }
      );
      if (!statusRes.ok) {
        const errData = await statusRes.json().catch(() => ({}));
        if ((errData as any).alreadyCompleted) {
          alert('⚠ Este paso ya fue completado por otro usuario.');
        } else if (statusRes.status === 409) {
          alert((errData as any).error || 'La orden está cerrada o el paso ya fue completado.');
        } else if (statusRes.status === 403) {
          alert((errData as any).error || 'No tienes permiso para completar pasos de rutas de fabricación. Contacta a tu supervisor.');
        } else {
          alert((errData as any).error || 'No se pudo guardar el paso como completado. Intenta de nuevo.');
        }
        // The server rejected the completion — nothing was persisted (status
        // and evidence are one transaction now), so don't mark it locally either.
        return;
      }
      const statusData = await statusRes.json().catch(() => ({}));

      // Generate Visor 360 event for completed process
      const eventEvidenceType = effectiveRecordType === 'TEXTO' ? 'TEXT' : 'IMAGE';
      let eventEvidenceText = '';
      let eventEvidenceUrl: string | null = null;

      // For images, only use the file path (not base64)
      if (eventEvidenceType === 'IMAGE' && evidenceValue && !evidenceValue.startsWith('data:')) {
        eventEvidenceUrl = `/api/storage/file?path=${encodeURIComponent(evidenceValue)}`;
        eventEvidenceText = `Evidencia imagen: ${route.process}`;
      } else if (eventEvidenceType === 'TEXT') {
        eventEvidenceText = evidenceValue.substring(0, 200); // Limit text to 200 chars
      }

      const eventRes = await fetch('/api/production-order-events', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productionOrderId: selectedOrder.id,
          orderNumber: selectedOrder.orderNumber,
          equipmentName: selectedOrder.machineName || route.machineName || null,
          equipmentId: resolveOrderEquipmentId(route?.equipmentId != null ? route : selectedOrder),
          processName: route.process,
          description: `Proceso completado: ${route.process}`,
          evidenceType: eventEvidenceType,
          evidenceText: eventEvidenceText || null,
          evidenceUrl: eventEvidenceUrl,
        }),
      });
      if (!eventRes.ok) {
        console.error('Error registering Visor 360 completion event:', await eventRes.text().catch(() => ''));
      }

      // Update the route status in the routes array
      const updatedRoutes = selectedOrder.manufacturingRoutes.map(r => 
        r.id === route.id 
          ? { ...r, status: 'completed' as const, completedAt: new Date().toISOString() }
          : r
      );
      
      // The evidence row is already in statusData - it came back from the
      // same transaction that just persisted it, no need to fetch it again.
      if ((statusData as any).evidence) {
        setRouteEvidenceMap({ ...routeEvidenceMap, [route.id]: (statusData as any).evidence });
      }

      // ── Check if ALL routes are now completed ──────────────────────────
      const allDone = updatedRoutes.length > 0 && updatedRoutes.every(r => r.status === 'completed');
      if (allDone) {
        try {
          const completeRes = await fetch(`/api/production-orders/${selectedOrder.id}/status`, {
            method: 'PATCH',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'COMPLETED', comment: 'Todos los procesos completados en Ejecución de Planta' }),
          });
          if (completeRes.ok) {
            // Invalidate planning board cache so it removes this order immediately
            queryClient.invalidateQueries({ queryKey: ['plannableOrders'] });
            // Remove from Plant Execution queue and reset view
            const finishedId = selectedOrder.id;
            setOrders(prev => prev.filter(o => o.id !== finishedId));
            setSelectedOrder(null);
            setSelectedRoute(null);
            setRecordData('');
            setVerificationChecked(false);
            setVerificationObservations('');
            alert(`✅ Orden ${selectedOrder.orderNumber} finalizada. Todos los procesos completados.`);
            return; // Early return — no need to update selected order below
          } else {
            console.error('Error marking order as completed:', await completeRes.text());
          }
        } catch (err) {
          console.error('Error marking order as completed:', err);
        }
      }
      // ──────────────────────────────────────────────────────────────────

      // Update order with new routes (only if not fully completed and navigated away)
      const updatedOrder = { ...selectedOrder, manufacturingRoutes: updatedRoutes };
      setSelectedOrder(updatedOrder);
      
      // Update orders list to reflect the change (use functional update to avoid stale closure)
      const completedOrderId = selectedOrder.id;
      setOrders(prev => prev.map(o => o.id === completedOrderId ? updatedOrder : o));
      
      setRecordData('');
      setVerificationChecked(false);
      setVerificationObservations('');
      setSelectedRoute(null);
    } catch (error) {
      console.error('Error completing route:', error);
      alert('Error al registrar la ruta completada');
    } finally {
      setIsUploading(false);
    }
  };

  // Register an operator for a route (shift assignment)
  const registerOperator = async (route: ManufacturingRoute, operatorName: string) => {
    if (!selectedOrder || !operatorName.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/production-order-events', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productionOrderId: selectedOrder.id,
          equipmentName: selectedOrder.machineName || route.machineName || null,
          equipmentId: resolveOrderEquipmentId(route?.equipmentId != null ? route : selectedOrder),
          processName: route.process,
          description: `Operador: ${operatorName.trim()} | Proceso: ${route.process}`,
          evidenceType: 'TEXT',
          evidenceText: `Operador en turno: ${operatorName.trim()}`,
        }),
      });
      setOperatorInputByRoute(prev => ({ ...prev, [route.id]: '' }));
      setShowOperatorFor(null);
      loadOrderEvents(selectedOrder.id);
    } catch (err) {
      console.error('Error registering operator:', err);
    }
  };

  // Image Zoom and Pan Functions
  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.5, 5));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 0.5, 0.5));
  };

  const handleResetZoom = () => {
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (imageZoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePan.x, y: e.clientY - imagePan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && imageZoom > 1) {
      setImagePan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setImageZoom(prev => Math.max(0.5, Math.min(5, prev + delta)));
  };

  const closeDesignModal = () => {
    setShowDesignImageModal(false);
    setDesignMeta(null);
    handleResetZoom();
  };

  const openDesignModal = async (order: ExecutionOrder) => {
    setDesignModalOrder(order);
    setDesignMeta(null);
    setShowDesignImageModal(true);
    if (order.productCode) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/catalog-designs/by-product-code/${encodeURIComponent(order.productCode)}/info`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.design) setDesignMeta(data.design);
        }
      } catch {}
    }
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById('print-content-detail');
    if (!element) {
      alert('El documento aún se está cargando, espera un momento e intenta de nuevo.');
      return;
    }
    if (!detailOrder) return;

    const opt = {
      margin: 0.3,
      filename: `Orden_Produccion_${detailOrder.orderNumber}_${detailOrder.lotCode || 'Sin_Lote'}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'in', 
        format: 'letter', 
        orientation: 'portrait' as const
      }
    };

    html2pdf().set(opt).from(element).save().catch((err: any) => {
      console.error('Error generating PDF:', err);
      alert('Error al generar el PDF. Por favor intenta de nuevo.');
    });
  };

  const openOrderDetail = (order: ExecutionOrder) => {
    setDetailOrder(order);
    setShowOrderDetailModal(true);
  };

  const handleSelectOrder = (order: ExecutionOrder) => {
    if (selectedOrder && selectedOrder.id !== order.id) stopPresence(selectedOrder.id);
    setSelectedOrder(order);
    loadOrderRoutes(order.id, order);
    loadAssignedMachines(order.id);
    loadAvailableEquipment();
    loadWeightRecords();
    startPresence(order.id);
    // ROT-207: reload the general plant note for whichever order is now selected
    let parsedNotes: any = {};
    try { if (order.notes) parsedNotes = JSON.parse(order.notes); } catch {}
    setPlantExecutionObservations(parsedNotes.plantExecutionObservations || '');
  };

  const savePlantExecutionObservations = async () => {
    if (!selectedOrder) return;
    setSavingPlantObservations(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/production-orders/${selectedOrder.id}/plant-notes`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plantExecutionObservations }),
      });
    } catch (e) {
      console.error('Error saving plant execution observations', e);
    } finally {
      setSavingPlantObservations(false);
    }
  };

  // Filter orders based on search query
  const filteredOrders = orders.filter((order) => {
    const query = searchQuery.toLowerCase();
    return (
      order.orderNumber.toLowerCase().includes(query) ||
      order.productName.toLowerCase().includes(query) ||
      order.customerName.toLowerCase().includes(query) ||
      (order.machineName && order.machineName.toLowerCase().includes(query))
    );
  });

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Ejecución de Planta</h1>
          <p className="text-gray-600 mt-2">Ejecuta las órdenes de producción asignadas a tu máquina</p>
        </div>

        <div className="flex gap-6 transition-all duration-300 items-start flex-row">
          {/* Orders Queue */}
          <div className={`transition-all duration-300 sticky top-6 ${effectiveCollapsed ? 'w-24 flex-shrink-0' : 'lg:w-1/3 w-full'}`}>
            <div className="bg-white rounded-lg shadow flex flex-col">
              <div className="p-4 border-b space-y-3">
                <div className="flex items-center justify-between">
                  {!effectiveCollapsed && <h2 className="text-lg font-semibold">Cola de Órdenes</h2>}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => (isNarrow ? setShowQueueDrawer(true) : setIsCollapsed(!isCollapsed))}
                    title={isNarrow ? 'Ver todas las órdenes' : isCollapsed ? 'Expandir' : 'Contraer'}
                    className="p-0 h-auto w-auto text-gray-600 hover:text-gray-900"
                  >
                    {isNarrow || isCollapsed ? (
                      <ChevronRight className="h-5 w-5" />
                    ) : (
                      <ChevronLeft className="h-5 w-5" />
                    )}
                  </Button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar orden, producto, cliente..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="divide-y overflow-y-auto transition-all max-h-[calc(100vh-220px)]">
                <OrderQueueList
                  orders={filteredOrders}
                  selectedOrder={selectedOrder}
                  compact={effectiveCollapsed}
                  searchQuery={searchQuery}
                  onSelect={handleSelectOrder}
                  onViewDesign={openDesignModal}
                  onViewOrder={openOrderDetail}
                />
              </div>
            </div>
          </div>

          {isNarrow && showQueueDrawer && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowQueueDrawer(false)}>
                <div className="fixed inset-0 bg-black/25" />
              </div>
              <div className="fixed inset-y-0 left-0 z-50 w-80 bg-white shadow-lg flex flex-col">
                <div className="p-4 border-b space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Cola de Órdenes</h2>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowQueueDrawer(false)}
                      title="Cerrar"
                      className="p-0 h-auto w-auto text-gray-600 hover:text-gray-900"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar orden, producto, cliente..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="divide-y overflow-y-auto flex-1">
                  <OrderQueueList
                    orders={filteredOrders}
                    selectedOrder={selectedOrder}
                    compact={false}
                    searchQuery={searchQuery}
                    onSelect={(order) => {
                      handleSelectOrder(order);
                      setShowQueueDrawer(false);
                    }}
                    onViewDesign={openDesignModal}
                    onViewOrder={openOrderDetail}
                  />
                </div>
              </div>
            </>
          )}

          {/* Manufacturing Routes */}
          <div className={`transition-all duration-300 ${effectiveCollapsed ? 'flex-1' : 'lg:w-2/3 w-full'}`}>
            {selectedOrder ? (
              <div className="space-y-4">
                {/* Order Header */}
                <div className="bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl shadow-lg overflow-hidden text-white">
                  {/* Top info strip */}
                  <div className="px-5 pt-5 pb-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="text-2xl font-extrabold tracking-tight leading-tight">{selectedOrder.orderNumber}</h2>
                        <p className="text-blue-100 mt-1 text-sm font-medium truncate">{selectedOrder.productName}</p>
                        {selectedOrder.customerName && (
                          <p className="text-xs text-blue-300 mt-0.5">Cliente: {selectedOrder.customerName}</p>
                        )}
                        {/* ROT-207: the order number now comes from ComerciaGS's own numbering,
                            so operators need the lote — their real reference — visible during
                            execution, not just buried in the checklist form. */}
                        {selectedOrder.lotCode && (
                          <p className="text-sm font-bold text-white mt-0.5">LOTE: {selectedOrder.lotCode}</p>
                        )}
                      </div>
                      {/* Status badge — derive from actualStartDate for accurate planning status */}
                      {(() => {
                        const hasStarted = !!selectedOrder.actualStartDate;
                        const label = hasStarted ? 'EN PRODUCCIÓN' : (selectedOrder.status?.replace(/_/g, ' ') ?? 'PENDIENTE');
                        const cls = hasStarted
                          ? 'bg-green-600 bg-opacity-80 border-green-400'
                          : 'bg-blue-600 bg-opacity-60 border-blue-400 border-opacity-40';
                        return (
                          <span className={`flex-shrink-0 text-xs font-bold uppercase tracking-wide border rounded-full px-3 py-1 ${cls}`}>
                            {label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Compact controls strip: Execution mode + Assigned Machines */}
                  <div className="border-t border-blue-600 border-opacity-40 px-5 py-3 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={toggleParallelMode}
                        className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none ${parallelExecutionMode ? 'bg-green-400' : 'bg-white bg-opacity-20'}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${parallelExecutionMode ? 'translate-x-5' : 'translate-x-1'}`} />
                      </button>
                      <span className="text-xs text-blue-100 font-medium">
                        {parallelExecutionMode ? 'Ejecución Paralela' : 'Ejecución Secuencial'}
                      </span>
                    </div>
                    <div className="w-px h-4 bg-blue-500 bg-opacity-60 self-center hidden sm:block" />
                    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                      <Cog className="h-3.5 w-3.5 text-blue-300 flex-shrink-0" />
                      {assignedMachines.length === 0 ? (
                        <span className="text-xs text-blue-300">
                          {selectedOrder.machineName ? selectedOrder.machineName : 'Sin máquinas adicionales'}
                        </span>
                      ) : (
                        assignedMachines.map(m => (
                          <span key={m.id} className="flex items-center gap-1 text-xs bg-white bg-opacity-15 border border-white border-opacity-20 rounded-full px-2 py-0.5 text-blue-100">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${m.status === 'COMPLETED' ? 'bg-green-400' : m.status === 'IN_PROGRESS' ? 'bg-blue-300 animate-pulse' : 'bg-gray-300'}`} />
                            {m.machineName}
                            <button onClick={() => removeMachineFromOrder(m.id)} className="text-blue-300 hover:text-red-300 ml-0.5 focus:outline-none">
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </span>
                        ))
                      )}
                      <button
                        onClick={() => setShowMachineModal(true)}
                        className="flex items-center gap-1 text-xs text-blue-200 hover:text-white bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-20 rounded-full px-2 py-0.5 transition-colors focus:outline-none"
                      >
                        <Plus className="h-3 w-3" />
                        Máquina
                      </button>
                    </div>
                  </div>

                  {/* ROT-207: general (not per-process) production note — persists on
                      the order itself, so it still shows once CLOSED and on reprint. */}
                  <div className="border-t border-blue-600 border-opacity-40 px-5 py-3">
                    <label className="block text-xs font-semibold text-blue-100 mb-1">
                      Observaciones Generales de Producción
                    </label>
                    <textarea
                      value={plantExecutionObservations}
                      onChange={(e) => setPlantExecutionObservations(e.target.value)}
                      onBlur={savePlantExecutionObservations}
                      placeholder="Notas generales de esta orden, visibles en la OP y su reimpresión..."
                      rows={2}
                      className="w-full bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg px-3 py-2 text-sm text-white placeholder-blue-200 focus:outline-none focus:bg-opacity-20 resize-none"
                    />
                    {savingPlantObservations && (
                      <p className="text-[10px] text-blue-200 mt-1">Guardando...</p>
                    )}
                  </div>
                </div>

                {/* ── Avance · Producto x Proceso: una tarjeta por proceso, aparece con su primer peso ── */}
                {(() => {
                  const routeCards = selectedOrder.manufacturingRoutes
                    .map(route => ({ route, breakdown: computeRouteWeightBreakdown(route, orderEvents) }))
                    .filter((rc): rc is { route: ManufacturingRoute; breakdown: NonNullable<typeof rc.breakdown> } => rc.breakdown !== null);

                  if (routeCards.length === 0) return null;

                  return (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Avance · Producto x Proceso</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {routeCards.map(({ route, breakdown }) => (
                          <Card key={route.id} className="overflow-hidden">
                            <CardHeader className="p-3 pb-2 space-y-0 bg-gray-50 border-b">
                              <span className="text-sm font-semibold text-gray-800 truncate">{route.process}</span>
                            </CardHeader>
                            <CardContent className="p-3 pt-2 space-y-2 text-xs">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-semibold text-gray-800">Producto en Proceso</span>
                                <span className="flex items-center gap-1.5 flex-shrink-0">
                                  {breakdown.totalBobinas > 0 && (
                                    <span className="font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">
                                      {breakdown.totalBobinas} bultos
                                    </span>
                                  )}
                                  <span className="font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">
                                    {breakdown.procesoKg.toFixed(1)} kg
                                  </span>
                                </span>
                              </div>
                              {breakdown.concepts.length > 1 && breakdown.concepts.map(({ concept, kg }) => (
                                <div key={concept} className="flex items-center justify-between gap-2 pl-3">
                                  <span className="text-gray-600 truncate">{concept}</span>
                                  <span className="font-semibold text-gray-800 tabular-nums flex-shrink-0">{kg.toFixed(1)} kg</span>
                                </div>
                              ))}

                              {breakdown.mermaKg > 0 && (
                                <>
                                  <div className="flex items-center justify-between gap-2 pt-1 border-t">
                                    <span className="font-semibold text-red-800">Producto en Merma</span>
                                    <span className="font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                                      {breakdown.mermaKg.toFixed(1)} kg
                                    </span>
                                  </div>
                                  {breakdown.mermaConcepts.map(({ concept, kg }) => (
                                    <div key={concept} className="flex items-center justify-between gap-2 pl-3">
                                      <span className="text-gray-600 truncate">{concept}</span>
                                      <span className="font-semibold text-gray-800 tabular-nums flex-shrink-0">{kg.toFixed(1)} kg</span>
                                    </div>
                                  ))}
                                </>
                              )}

                              {breakdown.productoTerminadoKg > 0 && (
                                <div className="flex items-center justify-between gap-2 pt-1 border-t">
                                  <span className="font-semibold text-green-800">Producto Terminado</span>
                                  <span className="font-bold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                                    {breakdown.productoTerminadoKg.toFixed(1)} kg
                                  </span>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Order-level time alert panel — with collapsible process details */}
                {selectedOrderAlert && (() => {
                  const isExceeded = liveElapsedMinutes > selectedOrderAlert.estimatedMinutes;
                  const overMin = Math.max(0, liveElapsedMinutes - selectedOrderAlert.estimatedMinutes);
                  const pct = Math.min(100, Math.round((liveElapsedMinutes / selectedOrderAlert.estimatedMinutes) * 100));
                  const fmtMins = (m: number) => {
                    const h = Math.floor(m / 60); const mn = m % 60;
                    return h > 0 ? `${h}h ${mn}m` : `${mn}m`;
                  };
                  const hasProcessAlerts = selectedProcessAlerts.length > 0;
                  const anyProcessExceeded = selectedProcessAlerts.some(pa => pa.isExceeded);
                  return (
                    <div className={`rounded-lg shadow ${isExceeded ? 'bg-red-50 border border-red-300' : 'bg-white'}`}>
                      {/* Main row — always visible */}
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Clock className={`h-4 w-4 ${isExceeded ? 'text-red-600' : 'text-blue-600'}`} />
                            <span className={`text-sm font-semibold ${isExceeded ? 'text-red-700' : 'text-gray-700'}`}>
                              Tiempo de Producción
                            </span>
                            {isExceeded && (
                              <span className="animate-pulse text-xs font-bold text-white bg-red-600 px-2 py-0.5 rounded-full">
                                ⏰ EXCEDIDO +{fmtMins(overMin)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs ${isExceeded ? 'text-red-600 font-bold' : 'text-gray-600'}`}>
                              {fmtMins(liveElapsedMinutes)} / {selectedOrderAlert.estimatedFormatted}
                            </span>
                            {hasProcessAlerts && (
                              <button
                                onClick={() => setShowProcessTimeDetails(v => !v)}
                                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded px-2 py-0.5 transition-colors"
                              >
                                {anyProcessExceeded && <span className="text-orange-500">⚠️</span>}
                                <span>{selectedProcessAlerts.length} proceso{selectedProcessAlerts.length !== 1 ? 's' : ''}</span>
                                <span>{showProcessTimeDetails ? '▲' : '▼'}</span>
                              </button>
                            )}
                          </div>
                        </div>
                        <div className={`w-full rounded-full h-2.5 ${isExceeded ? 'bg-red-200' : 'bg-gray-100'}`}>
                          <div
                            className={`h-2.5 rounded-full transition-all duration-500 ${isExceeded ? 'bg-red-500' : pct >= 80 ? 'bg-yellow-400' : 'bg-blue-500'}`}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-1 text-xs text-gray-400">
                          <span>Inicio: {new Date(selectedOrderAlert.actualStartDate).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span>
                          <span>
                            Vel. máquina: {selectedOrderAlert.velocidadMPM} m/min · 
                            Cant: {selectedOrderAlert.quantityPlanned.toFixed(0)} {selectedOrderAlert.unit}
                          </span>
                        </div>
                      </div>

                      {/* Collapsible process details */}
                      {showProcessTimeDetails && hasProcessAlerts && (
                        <div className="border-t border-gray-200 divide-y divide-gray-100">
                          {selectedProcessAlerts.map(pa => {
                            const nowMs = Date.now();
                            const elapsed = Math.floor((nowMs - new Date(pa.startedAt).getTime()) / 60000);
                            const hasEst = pa.estimatedMinutes != null && pa.estimatedMinutes > 0;
                            const ppct = hasEst ? Math.min(100, Math.round((elapsed / pa.estimatedMinutes) * 100)) : null;
                            const isExc = hasEst && elapsed > pa.estimatedMinutes;
                            const overM = isExc ? Math.max(0, elapsed - pa.estimatedMinutes) : 0;
                            const fm = (m: number) => { const h = Math.floor(m / 60); const mn = m % 60; return h > 0 ? `${h}h ${mn}m` : `${mn}m`; };
                            return (
                              <div key={pa.routeId} className={`px-4 py-3 ${isExc ? 'bg-orange-50' : ''}`}>
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-xs">⚙️</span>
                                    <span className={`text-xs font-medium truncate ${isExc ? 'text-orange-700' : 'text-gray-600'}`}>
                                      {pa.process}
                                    </span>
                                    {isExc && (
                                      <span className="flex-shrink-0 text-xs font-bold text-orange-600">
                                        +{fm(overM)}
                                      </span>
                                    )}
                                  </div>
                                  <span className={`text-xs flex-shrink-0 ml-2 ${isExc ? 'text-orange-600 font-bold' : 'text-gray-400'}`}>
                                    {fm(elapsed)}{hasEst ? ` / ${pa.estimatedFormatted}` : ''}
                                  </span>
                                </div>
                                {hasEst && ppct != null && (
                                  <div className={`w-full rounded-full h-1.5 ${isExc ? 'bg-orange-200' : 'bg-gray-100'}`}>
                                    <div
                                      className={`h-1.5 rounded-full transition-all duration-500 ${isExc ? 'bg-orange-500' : ppct >= 80 ? 'bg-yellow-400' : 'bg-green-500'}`}
                                      style={{ width: `${Math.min(ppct, 100)}%` }}
                                    />
                                  </div>
                                )}
                                <div className="text-xs text-gray-400 mt-0.5">
                                  Inicio: {new Date(pa.startedAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                  {pa.velocidadMPM > 0 && ` · ${pa.velocidadMPM} m/min`}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Process alerts for orders without order-level alert (no machine speed configured) */}
                {!selectedOrderAlert && selectedProcessAlerts.length > 0 && (() => {
                  const anyExc = selectedProcessAlerts.some(pa => pa.isExceeded);
                  const fmtMins = (m: number) => { const h = Math.floor(m / 60); const mn = m % 60; return h > 0 ? `${h}h ${mn}m` : `${mn}m`; };
                  return (
                    <div className="bg-white rounded-lg shadow">
                      <button
                        onClick={() => setShowProcessTimeDetails(v => !v)}
                        className="w-full flex items-center justify-between p-4 text-left"
                      >
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-semibold text-gray-700">Tiempo por Proceso</span>
                          {anyExc && <span className="animate-pulse text-xs font-bold text-white bg-orange-500 px-2 py-0.5 rounded-full">⚠️ EXCEDIDO</span>}
                        </div>
                        <span className="text-xs text-gray-500">{selectedProcessAlerts.length} proceso{selectedProcessAlerts.length !== 1 ? 's' : ''} {showProcessTimeDetails ? '▲' : '▼'}</span>
                      </button>
                      {showProcessTimeDetails && (
                        <div className="border-t border-gray-200 divide-y divide-gray-100">
                          {selectedProcessAlerts.map(pa => {
                            const elapsed = Math.floor((Date.now() - new Date(pa.startedAt).getTime()) / 60000);
                            const hasEst = pa.estimatedMinutes != null && pa.estimatedMinutes > 0;
                            const ppct = hasEst ? Math.min(100, Math.round((elapsed / pa.estimatedMinutes) * 100)) : null;
                            const isExc = hasEst && elapsed > pa.estimatedMinutes;
                            const overM = isExc ? Math.max(0, elapsed - pa.estimatedMinutes) : 0;
                            return (
                              <div key={pa.routeId} className={`px-4 py-3 ${isExc ? 'bg-orange-50' : ''}`}>
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-xs">⚙️</span>
                                    <span className={`text-xs font-medium truncate ${isExc ? 'text-orange-700' : 'text-gray-600'}`}>{pa.process}</span>
                                    {isExc && <span className="flex-shrink-0 text-xs font-bold text-orange-600">+{fmtMins(overM)}</span>}
                                  </div>
                                  <span className={`text-xs flex-shrink-0 ml-2 ${isExc ? 'text-orange-600 font-bold' : 'text-gray-400'}`}>
                                    {fmtMins(elapsed)}{hasEst ? ` / ${pa.estimatedFormatted}` : ''}
                                  </span>
                                </div>
                                {hasEst && ppct != null && (
                                  <div className={`w-full rounded-full h-1.5 ${isExc ? 'bg-orange-200' : 'bg-gray-100'}`}>
                                    <div className={`h-1.5 rounded-full transition-all duration-500 ${isExc ? 'bg-orange-500' : ppct >= 80 ? 'bg-yellow-400' : 'bg-green-500'}`} style={{ width: `${Math.min(ppct, 100)}%` }} />
                                  </div>
                                )}
                                <div className="text-xs text-gray-400 mt-0.5">
                                  Inicio: {new Date(pa.startedAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Presence banner: who else is viewing this order ── */}
                {presenceOthers.length > 0 && (
                  <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-lg px-4 py-2.5 text-sm text-amber-900 shadow-sm">
                    <span className="relative flex h-3 w-3 flex-shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                      <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-500"></span>
                    </span>
                    <div>
                      <span className="font-semibold">También está viendo esta orden: </span>
                      {presenceOthers.join(', ')}
                      <span className="text-amber-700 ml-1">— Coordina antes de guardar para no sobreescribir cambios.</span>
                    </div>
                  </div>
                )}

                {/* ── Bitácora en vivo (colapsable) ── */}
                {orderEvents.length > 0 && (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <button
                      onClick={() => setShowBitacora(v => !v)}
                      className="w-full flex items-center gap-2 px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <FileText className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                      <h3 className="font-semibold text-gray-800 text-sm">Bitácora de Actividad</h3>
                      <span className="ml-2 text-xs bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5 font-medium">
                        {orderEvents.length} eventos
                      </span>
                      <span className="ml-auto text-xs text-gray-400">
                        {showBitacora ? 'Ocultar' : 'Ver historial'}
                      </span>
                      <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${showBitacora ? 'rotate-90' : ''}`} />
                    </button>
                    {showBitacora && (
                      <div className="border-t border-gray-100 px-4 pb-3 pt-2 space-y-1 max-h-64 overflow-y-auto">
                        {[...orderEvents]
                          .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
                          .slice(0, 20)
                          .map((ev, i) => {
                            const ts = new Date(ev.occurredAt);
                            const timeStr = ts.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
                            const dateStr = ts.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
                            const isWeight = ev.description?.toLowerCase().includes('peso');
                            const isPause  = ev.description?.toLowerCase().includes('pausa');
                            const isStart  = ev.description?.toLowerCase().includes('inicio');
                            const dotColor = isWeight ? 'bg-teal-500' : isPause ? 'bg-orange-400' : isStart ? 'bg-blue-500' : 'bg-gray-400';
                            return (
                              <div key={i} className="flex items-start gap-2 text-xs py-1.5 border-b border-gray-50 last:border-0">
                                <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
                                <div className="flex-1 min-w-0">
                                  <span className="text-gray-700">{ev.description}</span>
                                  {ev.processName && (
                                    <span className="text-gray-400 ml-1">· {ev.processName}</span>
                                  )}
                                </div>
                                <div className="flex-shrink-0 text-right text-gray-400">
                                  <div>{timeStr}</div>
                                  <div className="text-gray-300">{dateStr}</div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}

                {/* CLOSED order banner — blocks all actions */}
                {selectedOrder.status === 'CLOSED' && (
                  <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 flex items-start gap-3">
                    <span className="text-2xl flex-shrink-0">🔒</span>
                    <div>
                      <p className="font-bold text-red-700 text-sm">Orden Cerrada</p>
                      <p className="text-red-600 text-xs mt-1">
                        Esta orden ya fue cerrada. No se pueden registrar pesos, completar pasos ni agregar eventos.
                      </p>
                    </div>
                  </div>
                )}

                {/* Manufacturing Routes */}
                <div className="space-y-3">
                  {selectedOrder.manufacturingRoutes.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 text-center">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-500">No hay rutas de fabricación</p>
                    </div>
                  ) : (
                    selectedOrder.manufacturingRoutes.map((route, idx) => (
                      <div
                        key={route.id}
                        className={`bg-white rounded-lg shadow p-4 border-l-4 transition-all ${
                          route.status === 'completed'
                            ? 'border-green-500 bg-green-50'
                            : selectedRoute?.id === route.id
                            ? 'border-blue-600 ring-2 ring-blue-300'
                            : !parallelExecutionMode && idx > 0 && selectedOrder.manufacturingRoutes[idx - 1]?.status !== 'completed'
                            ? 'border-gray-300 opacity-60'
                            : 'border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-bold bg-gray-100 px-3 py-1 rounded">
                              Paso {route.sequence}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{route.process}</p>
                              {canAssignMachine ? (
                                <select
                                  className="mt-1 text-xs border border-gray-200 rounded px-1.5 py-0.5 disabled:opacity-50"
                                  value={route.machineName || ''}
                                  onChange={e => assignRouteMachine(route, e.target.value)}
                                >
                                  <option value="">-- Sin asignar --</option>
                                  {availableEquipment.map((eq: any) => (
                                    <option key={eq.id} value={eq.name}>{eq.name}</option>
                                  ))}
                                  {route.machineName && !availableEquipment.some((eq: any) => eq.name === route.machineName) && (
                                    <option value={route.machineName}>{route.machineName}</option>
                                  )}
                                </select>
                              ) : (
                                route.machineName && (
                                  <p className="text-xs text-gray-600">Máquina: {route.machineName}</p>
                                )
                              )}
                              {route.recordType && (
                                <p className="text-xs text-blue-600 font-medium">
                                  Tipo: {route.recordType === 'TEXTO' ? 'Valor' : 'Fotografía'}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!route.isVerificationOnly && (route.status === 'in_progress' || (selectedRoute?.id === route.id && selectedRoute?.status === 'in_progress')) && (
                              <Button
                                onClick={() => handleWeightClick(route)}
                                size="sm"
                                variant="outline"
                                className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100 hover:border-amber-400"
                                title="Registrar Peso"
                              >
                                <Scale className="h-4 w-4 mr-1" />
                                Peso
                              </Button>
                            )}
                            {route.enablesChecklist && (route.status === 'in_progress' || route.status === 'completed' || (selectedRoute?.id === route.id && selectedRoute?.status === 'in_progress')) && (
                              <Button
                                onClick={openChecklistForEdit}
                                size="sm"
                                variant="outline"
                                className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 hover:border-blue-400"
                                title="Ver / Editar CheckList"
                              >
                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                                Checklist
                              </Button>
                            )}
                            {route.status === 'completed' && (
                              <CheckCircle className="h-6 w-6 text-green-600" />
                            )}
                            {(route.status === 'in_progress' || (selectedRoute?.id === route.id && selectedRoute?.status === 'in_progress')) && (
                              <div className="animate-spin">
                                <Play className="h-6 w-6 text-blue-600" />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Per-route weight summary */}
                        {(() => {
                          const breakdown = computeRouteWeightBreakdown(route, orderEvents);
                          if (!breakdown) return null;
                          return (
                            <div className="mt-2 mb-2 flex flex-wrap gap-1">
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                                Proceso: {breakdown.procesoKg.toFixed(1)} kg
                              </span>
                              {breakdown.mermaKg > 0 && (
                                <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-medium">
                                  Merma: {breakdown.mermaKg.toFixed(1)} kg
                                </span>
                              )}
                              {breakdown.productoTerminadoKg > 0 && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">
                                  Terminado: {breakdown.productoTerminadoKg.toFixed(1)} kg
                                </span>
                              )}
                              {breakdown.totalBobinas > 0 && (
                                <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full font-medium">
                                  Bultos: {breakdown.totalBobinas}
                                </span>
                              )}
                              {breakdown.concepts.map(({ concept, kg }) => (
                                <span key={concept} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                                  {concept}: {kg.toFixed(1)} kg
                                </span>
                              ))}
                              {breakdown.mermaConcepts.map(({ concept, kg }) => (
                                <span key={`merma-${concept}`} className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full">
                                  Merma · {concept}: {kg.toFixed(1)} kg
                                </span>
                              ))}
                            </div>
                          );
                        })()}

                        {/* ROT-201: curing-time tracking (inicio/fin/duración) — independent
                            of isVerificationOnly so it shows on top of weight/bobinas capture
                            (e.g. Curado) instead of only on verification-only steps. Rendered
                            outside the evidence-gated block below because a weight-capturing
                            route never gets a manufacturing_route_evidence row. */}
                        {route.registraTiempoCurado && !route.isVerificationOnly && route.status === 'completed' && route.startedAt && route.completedAt && (
                          <div className="text-xs text-gray-700 bg-white p-2 rounded border border-green-200 mb-2 space-y-0.5">
                            <div>⏱️ Inicio: {new Date(route.startedAt).toLocaleString('es-ES')}</div>
                            <div>🏁 Fin: {new Date(route.completedAt).toLocaleString('es-ES')}</div>
                            <div>⏳ Duración: {formatElapsedDuration(route.startedAt, route.completedAt)}</div>
                          </div>
                        )}

                        {/* Completed Evidence Display */}
                        {route.status === 'completed' && routeEvidenceMap[route.id] && (
                          <div className="bg-green-50 p-3 rounded border border-green-300 mb-3">
                            <div className="text-xs font-semibold text-green-700 mb-2">✓ Completado</div>
                            {route.isVerificationOnly && route.startedAt && route.completedAt && (
                              <div className="text-xs text-gray-700 bg-white p-2 rounded border border-green-200 mb-2 space-y-0.5">
                                <div>⏱️ Inicio: {new Date(route.startedAt).toLocaleString('es-ES')}</div>
                                <div>🏁 Fin: {new Date(route.completedAt).toLocaleString('es-ES')}</div>
                                <div>⏳ Duración: {formatElapsedDuration(route.startedAt, route.completedAt)}</div>
                              </div>
                            )}
                            {routeEvidenceMap[route.id].type === 'TEXT' ? (
                              <div className="text-sm text-gray-800 bg-white p-2 rounded border border-green-200 whitespace-pre-wrap">
                                {routeEvidenceMap[route.id].value}
                              </div>
                            ) : (
                              <img 
                                src={routeEvidenceMap[route.id].value} 
                                alt="Evidencia capturada"
                                className="w-full max-h-40 object-cover rounded border border-green-200"
                              />
                            )}
                            {routeEvidenceMap[route.id].createdAt && (
                              <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-green-200">
                                <div>📅 {new Date(routeEvidenceMap[route.id].createdAt).toLocaleDateString('es-ES')}</div>
                                <div>⏰ {new Date(routeEvidenceMap[route.id].createdAt).toLocaleTimeString('es-ES')}</div>
                                {routeEvidenceMap[route.id].userId && (
                                  <div>👤 Registrado por: {routeEvidenceMap[route.id].userId}</div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Operators registered for this route */}
                        {(() => {
                          const routeOperatorEvents = orderEvents.filter(
                            e => e.processName === route.process && e.description.startsWith('Operador:')
                          );
                          return (
                            <div className="mb-3">
                              {routeOperatorEvents.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {routeOperatorEvents.map((e: any) => {
                                    const name = e.description.replace(/^Operador:\s*/, '').split(' | ')[0];
                                    return (
                                      <span key={e.id} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full">
                                        👤 {name}
                                        <span className="text-indigo-400 text-xs">{new Date(e.occurredAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                              {(route.status === 'in_progress' || (selectedRoute?.id === route.id && selectedRoute?.status === 'in_progress')) && (
                                showOperatorFor === route.id ? (
                                  <div className="flex gap-2 items-center">
                                    {/* ROT-211: searchable by número or nombre — same
                                        component/pattern as the weight modal's operator
                                        field below, previously only a plain <select> here. */}
                                    <div className="flex-1">
                                      <SearchableSelect
                                        options={catalogEmployees.map(emp => ({
                                          value: emp.name,
                                          label: `${emp.numEmpleado ? emp.numEmpleado + ' - ' : ''}${emp.name}${emp.puesto ? ' (' + emp.puesto + ')' : ''}`,
                                          searchText: `${emp.numEmpleado ?? ''} ${emp.name}`,
                                        }))}
                                        value={operatorInputByRoute[route.id] || ''}
                                        onChange={(val) => setOperatorInputByRoute(prev => ({ ...prev, [route.id]: val }))}
                                        placeholder="-- Busca por número o nombre --"
                                      />
                                    </div>
                                    <button
                                      onClick={() => registerOperator(route, operatorInputByRoute[route.id] || '')}
                                      className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700"
                                    >
                                      Registrar
                                    </button>
                                    <button onClick={() => setShowOperatorFor(null)} className="text-gray-400 hover:text-gray-600 text-sm px-1">✕</button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setShowOperatorFor(route.id)}
                                    className="text-indigo-600 hover:text-indigo-800 text-xs flex items-center gap-1 underline"
                                  >
                                    👤 {routeOperatorEvents.length > 0 ? 'Cambio de turno / Agregar operador' : 'Registrar operador en turno'}
                                  </button>
                                )
                              )}
                            </div>
                          );
                        })()}

                        {/* Route Actions */}
                        {selectedRoute?.id === route.id && selectedRoute?.status === 'in_progress' && (
                          <div className="space-y-3 bg-blue-50 p-3 rounded">
                            {/* ROT-201: shows alongside whatever capture UI follows below —
                                the isVerificationOnly branch already shows its own copy of
                                this line, so it's excluded here to avoid a duplicate. */}
                            {route.registraTiempoCurado && !route.isVerificationOnly && route.startedAt && (
                              <div className="text-xs text-gray-600">
                                ⏱️ Inicio: {new Date(route.startedAt).toLocaleString('es-ES')} · En curso: {formatElapsedDuration(route.startedAt)}
                              </div>
                            )}
                            {/* Record Data Input */}
                            {route.isVerificationOnly ? (
                              <div className="space-y-2">
                                {route.startedAt && (
                                  <div className="text-xs text-gray-600">
                                    ⏱️ Inicio: {new Date(route.startedAt).toLocaleString('es-ES')} · En curso: {formatElapsedDuration(route.startedAt)}
                                  </div>
                                )}
                                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={verificationChecked}
                                    onChange={(e) => setVerificationChecked(e.target.checked)}
                                    disabled={isUploading}
                                    className="h-4 w-4"
                                  />
                                  {route.process} realizado correctamente
                                </label>
                                <div>
                                  <label className="block text-sm font-medium mb-1">Observaciones (opcional)</label>
                                  <textarea
                                    value={verificationObservations}
                                    onChange={(e) => setVerificationObservations(e.target.value)}
                                    placeholder="Observaciones sobre esta etapa..."
                                    className="w-full p-2 border rounded text-sm h-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={isUploading}
                                  />
                                </div>
                              </div>
                            ) : route.recordType === 'TEXTO' ? (
                              <div>
                                <label className="block text-sm font-medium mb-2">
                                  <FileText className="h-4 w-4 inline mr-2" />
                                  Registrar Valor del Proceso
                                </label>
                                <textarea
                                  value={recordData}
                                  onChange={(e) => setRecordData(e.target.value)}
                                  placeholder="Ingresa el valor o resultado de este paso..."
                                  className="w-full p-2 border rounded text-sm h-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={isUploading}
                                />
                              </div>
                            ) : route.recordType === 'IMAGEN' ? (
                              <div>
                                <label className="block text-sm font-medium mb-2">
                                  <Camera className="h-4 w-4 inline mr-2" />
                                  Capturar Fotografía del Proceso
                                </label>
                                {recordData ? (
                                  <div className="relative">
                                    <img 
                                      src={recordData} 
                                      alt="Fotografía capturada" 
                                      className="w-full h-32 object-cover rounded border-2 border-blue-300"
                                    />
                                    <button
                                      onClick={() => setRecordData('')}
                                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                                      disabled={isUploading}
                                    >
                                      ×
                                    </button>
                                  </div>
                                ) : (
                                  <div className="border-2 border-dashed rounded p-4 text-center bg-white">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      capture="environment"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          const reader = new FileReader();
                                          reader.onload = (event) => {
                                            setRecordData(event.target?.result as string);
                                          };
                                          reader.readAsDataURL(file);
                                        }
                                      }}
                                      className="hidden"
                                      id={`upload-${route.id}`}
                                      disabled={isUploading}
                                    />
                                    <label htmlFor={`upload-${route.id}`} className={`cursor-pointer block ${isUploading ? 'opacity-50' : ''}`}>
                                      <Camera className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                      <p className="text-sm text-gray-600">Toca para capturar foto</p>
                                    </label>
                                  </div>
                                )}
                              </div>
                            ) : null}

                            <div className="flex gap-2">
                              {canCompleteRoute ? (
                                <Button
                                  onClick={() => completeRoute(route)}
                                  className="flex-1 bg-green-600 hover:bg-green-700"
                                  disabled={isUploading || selectedOrder.status === 'CLOSED'}
                                >
                                  {isUploading ? (
                                    <>
                                      <Upload className="h-4 w-4 mr-2 animate-spin" />
                                      Guardando...
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Completar
                                    </>
                                  )}
                                </Button>
                              ) : (
                                <div className="flex-1 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700">
                                  <Lock className="h-3.5 w-3.5 flex-shrink-0" />
                                  Solo anotar peso — completar requiere permiso de supervisor
                                </div>
                              )}
                              <Button
                                onClick={() => handlePauseClick(route)}
                                variant="outline"
                                className="flex-1"
                                disabled={isUploading || selectedOrder.status === 'CLOSED'}
                              >
                                <Pause className="h-4 w-4 mr-2" />
                                Pausar
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Start Button */}
                        {route.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => startRoute(route)}
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              disabled={
                                selectedOrder.status === 'CLOSED' ||
                                (!parallelExecutionMode && idx > 0 && selectedOrder.manufacturingRoutes[idx - 1]?.status !== 'completed')
                              }
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Iniciar
                            </Button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">Selecciona una orden para comenzar</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Design Image Modal - Full Screen View with Zoom Controls */}
      {showDesignImageModal && designModalOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-2xl w-full h-full max-w-[98vw] max-h-[98vh] flex flex-col">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gradient-to-r from-purple-50 to-indigo-50 flex-shrink-0">
              <div className="flex-1 min-w-0 mr-4">
                <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <FileImage className="w-6 h-6 text-purple-600" />
                  Diseño / Plano
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Orden #{designModalOrder.orderNumber} - {designModalOrder.productName}
                </p>
                {designMeta && (
                  <div className="mt-2 flex flex-wrap items-start gap-3">
                    <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-semibold px-2.5 py-1 rounded-full border border-purple-200">
                      📌 Versión {designMeta.version}
                    </span>
                    {designMeta.imageDate && (
                      <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full border border-blue-200">
                        📅 {new Date(designMeta.imageDate).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    {(designMeta.uploaderName || designMeta.uploaderUsername) && (
                      <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full border border-gray-200">
                        👤 {designMeta.uploaderName || designMeta.uploaderUsername}
                      </span>
                    )}
                    {designMeta.comments && (
                      <div className="w-full mt-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                        <span className="text-amber-500 text-sm flex-shrink-0 mt-0.5">💬</span>
                        <div>
                          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-0.5">Comentarios del diseñador</p>
                          <p className="text-sm text-amber-900 leading-snug">{designMeta.comments}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Zoom Controls */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 bg-white rounded-lg shadow-md px-2 py-1 mr-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleZoomOut}
                    disabled={imageZoom <= 0.5}
                    className="h-8 w-8 p-0"
                    title="Alejar (Zoom Out)"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium min-w-[60px] text-center">
                    {Math.round(imageZoom * 100)}%
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleZoomIn}
                    disabled={imageZoom >= 5}
                    className="h-8 w-8 p-0"
                    title="Acercar (Zoom In)"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleResetZoom}
                    className="h-8 w-8 p-0 ml-1"
                    title="Restablecer Vista"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
                
                <button
                  onClick={closeDesignModal}
                  className="text-gray-500 hover:text-gray-700 text-3xl font-bold w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                  title="Cerrar (ESC)"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div 
              className="flex-1 overflow-hidden bg-gray-100 p-4 relative"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
            >
              <div className="w-full h-full flex items-center justify-center">
                {(() => {
                  const hasOrderDesign = designModalOrder.designUrl && designModalOrder.designUrl.startsWith('ordenesproduccion/');
                  const hasCatalogDesign = !hasOrderDesign && designModalOrder.productCode;
                  const imgSrc = hasOrderDesign
                    ? `/api/production-orders/${designModalOrder.id}/design-image?t=${Date.now()}`
                    : hasCatalogDesign
                    ? `/api/catalog-designs/by-product-code/${encodeURIComponent(designModalOrder.productCode!)}/image?t=${Date.now()}`
                    : null;

                  return imgSrc ? (
                    <img
                      src={imgSrc}
                      alt="Diseño de la orden"
                      className={`max-w-none select-none ${imageZoom > 1 ? 'cursor-move' : 'cursor-default'}`}
                      style={{
                        transform: `scale(${imageZoom}) translate(${imagePan.x / imageZoom}px, ${imagePan.y / imageZoom}px)`,
                        transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                        maxHeight: '80vh',
                        maxWidth: '80vw',
                      }}
                      draggable={false}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const errorMsg = document.createElement('div');
                          errorMsg.className = 'text-center text-gray-500 py-16 bg-white rounded-lg p-12 shadow-lg';
                          errorMsg.innerHTML = `
                            <div class="text-8xl mb-4">📄</div>
                            <p class="text-xl font-semibold mb-2">No hay imagen de diseño disponible</p>
                            <p class="text-gray-600">Sube una imagen desde el catálogo de diseños o desde el editor de la orden.</p>
                          `;
                          parent.appendChild(errorMsg);
                        }
                      }}
                    />
                  ) : (
                    <div className="text-center text-gray-500 py-16 bg-white rounded-lg p-12 shadow-lg">
                      <div className="text-8xl mb-4">📄</div>
                      <p className="text-xl font-semibold mb-2">No hay imagen de diseño disponible</p>
                      <p className="text-gray-600">Sube una imagen desde el catálogo de diseños o desde el editor de la orden.</p>
                    </div>
                  );
                })()}
              </div>
            </div>
            
            <div className="flex justify-between items-center gap-2 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Move className="w-4 h-4" />
                  <span>{imageZoom > 1 ? 'Arrastra para mover' : 'Usa los controles de zoom'}</span>
                </div>
                <span>•</span>
                <span>Rueda del mouse para zoom rápido</span>
              </div>
              <Button
                variant="outline"
                onClick={closeDesignModal}
                className="min-w-[120px]"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal - Full Print Document */}
      {showOrderDetailModal && detailOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-auto">
            <div className="sticky top-0 z-10 p-4 border-b flex justify-between items-center bg-white no-print">
              <h3 className="text-2xl font-bold text-gray-800">Vista Previa de Impresión</h3>
              <div className="flex gap-2">
                <Button onClick={handleDownloadPDF} className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2">
                  <FileDown className="w-4 h-4" />
                  Descargar PDF
                </Button>
                <Button
                  onClick={() => window.print()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  🖨️ Imprimir
                </Button>
                <button onClick={() => setShowOrderDetailModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold px-3">×</button>
              </div>
            </div>

            <ProductionOrderDocument orderId={detailOrder.id} printContentId="print-content-detail" />
            
            <div className="flex justify-end gap-4 p-4 border-t bg-gray-50 no-print">
              <Button variant="outline" onClick={() => setShowOrderDetailModal(false)}>Cerrar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Pause Modal */}
      {showPauseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b bg-gradient-to-r from-orange-50 to-yellow-50">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Pause className="w-5 h-5 text-orange-600" />
                Pausar Proceso
              </h3>
              {routeToPause && (
                <p className="text-sm text-gray-600 mt-1">
                  {routeToPause.process} - Orden #{selectedOrder?.orderNumber}
                </p>
              )}
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Previous pauses for this route */}
              {(() => {
                const prevPauses = orderEvents.filter(
                  e => e.processName === routeToPause?.process && e.description.startsWith('Pausa en proceso')
                );
                if (prevPauses.length === 0) return null;
                return (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-orange-700 mb-2">Pausas anteriores en este proceso:</p>
                    <div className="space-y-1">
                      {prevPauses.map((e: any) => {
                        const dur = getPauseDuration(e, orderEvents);
                        const motivo = e.description.replace(/^Pausa en proceso:[^-]+-\s*/, '').split(' | ')[0];
                        return (
                          <div key={e.id} className="text-xs text-gray-700 flex justify-between items-start gap-2 bg-white border border-orange-100 rounded px-2 py-1">
                            <span className="flex-1">{motivo}</span>
                            <span className="text-orange-600 font-medium whitespace-nowrap">{dur}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Operador <span className="text-red-500">*</span>
                </label>
                <select
                  value={pauseOperator}
                  onChange={(e) => setPauseOperator(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  autoFocus
                >
                  <option value="">-- Selecciona un operador --</option>
                  {catalogEmployees.map((emp) => (
                    <option key={emp.id} value={emp.name}>
                      {emp.numEmpleado ? `${emp.numEmpleado} - ` : ''}{emp.name}{emp.puesto ? ` (${emp.puesto})` : ''}
                    </option>
                  ))}
                </select>
                {catalogEmployees.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">No hay operadores configurados en el catálogo.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo de pausa <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedPauseReasonId}
                  onChange={(e) => setSelectedPauseReasonId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">-- Selecciona un motivo --</option>
                  {catalogPauseReasonsList.map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.code} - {r.name}
                    </option>
                  ))}
                </select>
                {catalogPauseReasonsList.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">No hay motivos configurados en el catálogo.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observaciones <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <textarea
                  value={pauseObservations}
                  onChange={(e) => setPauseObservations(e.target.value)}
                  placeholder="Observaciones adicionales sobre la pausa..."
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPauseModal(false);
                  setPauseObservations('');
                  setPauseOperator('');
                  setSelectedPauseReasonId('');
                  setRouteToPause(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmPause}
                className="bg-orange-600 hover:bg-orange-700"
                disabled={!selectedPauseReasonId || !pauseOperator.trim()}
              >
                <Pause className="h-4 w-4 mr-2" />
                Confirmar Pausa
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Weight Registration Modal */}
      {showWeightModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b bg-gradient-to-r from-amber-50 to-yellow-50">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Scale className="w-5 h-5 text-amber-600" />
                Registrar Peso
              </h3>
              {routeForWeight && (
                <p className="text-sm text-gray-600 mt-1">
                  {routeForWeight.process} - Orden #{selectedOrder?.orderNumber}
                </p>
              )}
            </div>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              {/* Previous weights for this route */}
              {(() => {
                const prevWeights = orderEvents.filter(
                  e => e.processName === routeForWeight?.process && e.description.includes('Peso registrado:')
                );
                if (prevWeights.length === 0) return null;
                return (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-700 mb-2">Pesos registrados anteriormente:</p>
                    <div className="space-y-1">
                      {prevWeights.map((e: any) => {
                        const match = e.description.match(/Peso registrado:\s*(-?[\d.,]+\s*\w+)/);
                        const pesoStr = match ? match[1] : e.description;
                        const opMatch = e.description.match(/Operador:\s*([^|]+)/);
                        return (
                          <div key={e.id} className="text-xs flex justify-between items-center bg-white border border-amber-100 rounded px-2 py-1">
                            <span className="font-medium text-gray-800">{pesoStr}</span>
                            <span className="text-gray-500">{opMatch ? `👤 ${opMatch[1].trim()} · ` : ''}{new Date(e.occurredAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Máquina <span className="text-red-500">*</span>
                </label>
                <select
                  value={weightMachineName}
                  onChange={(e) => setWeightMachineName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">-- Selecciona una máquina --</option>
                  {availableEquipment.map((eq: any) => (
                    <option key={eq.id} value={eq.name}>{eq.name}</option>
                  ))}
                  {weightMachineName && !availableEquipment.some((eq: any) => eq.name === weightMachineName) && (
                    <option value={weightMachineName}>{weightMachineName}</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Operador <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <SearchableSelect
                  options={catalogEmployees.map(emp => ({
                    value: emp.name,
                    label: `${emp.numEmpleado ? emp.numEmpleado + ' - ' : ''}${emp.name}${emp.puesto ? ' (' + emp.puesto + ')' : ''}`,
                    searchText: `${emp.numEmpleado ?? ''} ${emp.name}`,
                  }))}
                  value={weightOperator}
                  onChange={setWeightOperator}
                  placeholder="-- Busca por número o nombre --"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Camera className="h-4 w-4 text-amber-600" />
                  Foto de Báscula
                  {canManualWeight ? (
                    <span className="text-xs text-gray-400 font-normal">(opcional – detecta peso automáticamente)</span>
                  ) : (
                    <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full">OBLIGATORIO</span>
                  )}
                </label>
                {!canManualWeight && !scaleImage && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-2">
                    <Lock className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-700">
                      Los operadores deben fotografiar la báscula para registrar el peso. El valor puede corregirse si la IA no lo detecta correctamente.
                    </p>
                  </div>
                )}
                {scaleImage ? (
                  <div className="relative">
                    <img 
                      src={scaleImage} 
                      alt="Báscula" 
                      className="w-full h-32 object-cover rounded-lg border-2 border-amber-300"
                    />
                    {isAnalyzingImage ? (
                      <div className="absolute inset-0 bg-black/60 rounded-lg flex flex-col items-center justify-center gap-2">
                        <div className="h-8 w-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
                        <p className="text-white text-xs font-medium text-center px-2">Analizando con IA…</p>
                        <p className="text-white/70 text-xs text-center px-2">Puede tardar 5–15 segundos</p>
                      </div>
                    ) : (
                      <div className="absolute top-2 right-2 flex gap-2">
                        <button
                          onClick={() => analyzeScaleImage(scaleImage)}
                          className="bg-blue-600 text-white rounded-full px-3 py-1 text-xs hover:bg-blue-700 flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          Detectar Peso
                        </button>
                        <button
                          onClick={() => setScaleImage('')}
                          className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-amber-200 rounded-lg p-4 text-center bg-amber-50">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            setScaleImage(event.target?.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="hidden"
                      id="scale-image-upload"
                      disabled={isAnalyzingImage}
                    />
                    <label htmlFor="scale-image-upload" className="cursor-pointer block">
                      <Camera className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                      <p className="text-sm text-amber-700 font-medium">Toca para fotografiar la báscula</p>
                      <p className="text-xs text-amber-500 mt-1">La IA detectará el peso automáticamente</p>
                    </label>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Peso <span className="text-red-500">*</span>
                  {!canManualWeight && !scaleImage && (
                    <span className="ml-2 text-xs text-gray-400 font-normal flex items-center gap-1 inline-flex">
                      <Lock className="h-3 w-3" /> Se llenará automáticamente al analizar la foto
                    </span>
                  )}
                  {analysisConfidence && (
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                      analysisConfidence === 'high' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {analysisConfidence === 'high' ? 'Detectado con alta confianza' : 'Verificar valor'}
                    </span>
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    value={weightValue}
                    onChange={(e) => {
                      setWeightValue(e.target.value);
                      setAnalysisConfidence(null);
                    }}
                    placeholder={!canManualWeight && !scaleImage ? 'Toma la foto primero...' : 'Ej: 25.5'}
                    readOnly={!canManualWeight && !scaleImage}
                    className={`flex-1 border rounded-lg p-3 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 ${
                      !canManualWeight && !scaleImage
                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                        : analysisConfidence === 'high' 
                          ? 'border-green-400 bg-green-50' 
                          : analysisConfidence === 'low'
                          ? 'border-yellow-400 bg-yellow-50'
                          : 'border-gray-300'
                    }`}
                  />
                  <select
                    value={weightUnit}
                    onChange={(e) => setWeightUnit(e.target.value)}
                    disabled={!canManualWeight && !scaleImage}
                    className={`border rounded-lg px-3 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 ${
                      !canManualWeight && !scaleImage ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300'
                    }`}
                  >
                    <option value="kg">kg</option>
                    <option value="lb">lb</option>
                    <option value="g">g</option>
                    <option value="oz">oz</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Concepto de Peso <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  options={availableWeightRecords.map(wr => ({
                    value: wr.name,
                    label: `${wr.code} - ${wr.name}`,
                    searchText: `${wr.code} ${wr.name}`,
                  }))}
                  value={weightConcept}
                  onChange={(name) => {
                    setWeightConcept(name);
                    const found = availableWeightRecords.find(wr => wr.name === name);
                    setWeightSubConcept('');
                    setWeightSubConcepts([]);
                    if (found?.id) loadWeightSubConcepts(found.id);
                  }}
                  placeholder="-- Busca por código o nombre --"
                />
                {availableWeightRecords.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Configure los conceptos de peso en Catálogos → Registros de Peso
                  </p>
                )}
              </div>

              {weightConcept && (isLoadingSubConcepts || weightSubConcepts.length > 0) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subconcepto de Peso <span className="text-red-500">*</span>
                  </label>
                  {isLoadingSubConcepts ? (
                    <div className="w-full border border-gray-300 rounded-lg p-3 text-sm text-gray-400 bg-gray-50">
                      Cargando subconceptos...
                    </div>
                  ) : (
                    <select
                      value={weightSubConcept}
                      onChange={(e) => setWeightSubConcept(e.target.value)}
                      className="w-full border border-amber-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-amber-50"
                    >
                      <option value="">-- Seleccione un subconcepto --</option>
                      {weightSubConcepts.map((sc) => (
                        <option key={sc.id} value={sc.name}>
                          {sc.code} - {sc.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              {routeForWeight?.solicitaBobinas && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cantidad de Bobinas
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={bobinasCount}
                    onChange={(e) => setBobinasCount(e.target.value)}
                    placeholder="Número de bobinas"
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observaciones <span className="text-gray-400">(opcional)</span>
                </label>
                <textarea
                  value={weightObservations}
                  onChange={(e) => setWeightObservations(e.target.value)}
                  placeholder="Notas adicionales sobre el pesaje..."
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={() => {
                  setShowWeightModal(false);
                  setWeightValue('');
                  setWeightObservations('');
                  setBobinasCount('');
                  setWeightConcept('');
                  setWeightSubConcept('');
                  setWeightSubConcepts([]);
                  setRouteForWeight(null);
                  setScaleImage('');
                  setAnalysisConfidence(null);
                }}
                disabled={isRecordingWeight || isAnalyzingImage}
              >
                Cancelar
              </Button>
              <Button
                onClick={recordWeight}
                className="bg-amber-600 hover:bg-amber-700"
                disabled={
                  !weightValue.trim() ||
                  !weightConcept ||
                  (weightSubConcepts.length > 0 && !weightSubConcept) ||
                  isRecordingWeight ||
                  (!canManualWeight && !scaleImage)
                }
              >
                {isRecordingWeight ? (
                  <>
                    <Upload className="h-4 w-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Scale className="h-4 w-4 mr-2" />
                    Grabar Peso
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Machine Modal */}
      {showMachineModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Cog className="w-5 h-5 text-purple-600" />
                  Agregar Máquina
                </h3>
                <button
                  onClick={() => {
                    setShowMachineModal(false);
                    setNewMachineName('');
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Orden: {selectedOrder.orderNumber}
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seleccionar Máquina
                </label>
                <select
                  value={newMachineName}
                  onChange={(e) => setNewMachineName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">-- Seleccione una máquina --</option>
                  {availableEquipment.map((eq) => (
                    <option key={eq.id} value={eq.name}>
                      {eq.code} - {eq.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-sm text-gray-600 bg-purple-50 p-3 rounded-lg">
                <p className="font-medium text-purple-800 mb-1">Ejecución Multimáquina</p>
                <p>Al agregar múltiples máquinas, esta orden puede ejecutarse simultáneamente en cada una de ellas para cumplir con urgencias de entrega.</p>
              </div>

              {assignedMachines.length > 0 && (
                <div className="text-sm">
                  <p className="font-medium text-gray-700 mb-2">Máquinas ya asignadas:</p>
                  <div className="flex flex-wrap gap-1">
                    {assignedMachines.map((m) => (
                      <span key={m.id} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                        {m.machineName}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-lg">
              <Button
                onClick={() => {
                  setShowMachineModal(false);
                  setNewMachineName('');
                }}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={addMachineToOrder}
                className="bg-purple-600 hover:bg-purple-700"
                disabled={!newMachineName.trim()}
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar Máquina
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Checklist Modal */}
      {showChecklistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg shadow-xl w-[95vw] max-w-6xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-bold uppercase tracking-wider text-center w-full">
                {isEditingChecklist ? 'VER / EDITAR CHECKLIST' : 'CHECKLIST DE OPERACIÓN'}
              </h2>
              <button onClick={() => { setShowChecklistModal(false); setIsEditingChecklist(false); setChecklistHistoryBanner(null); }} className="text-gray-400 hover:text-gray-600 absolute right-4">
                <X className="w-6 h-6" />
              </button>
            </div>

            {checklistHistoryBanner && (
              <div className="mx-4 mt-2 mb-0 flex items-center gap-2 rounded-lg bg-green-50 border border-green-300 px-4 py-2 text-sm text-green-800">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span>
                  Datos pre-llenados del último trabajo exitoso: <strong>{checklistHistoryBanner}</strong>. Puedes modificar cualquier campo antes de guardar.
                </span>
                <button onClick={() => setChecklistHistoryBanner(null)} className="ml-auto text-green-600 hover:text-green-800">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            <div className="p-4 overflow-y-auto flex-1 text-sm">
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="col-span-2">
                  <label className="font-semibold text-gray-700 block mb-1">CLIENTE:</label>
                  <input type="text" value={checklistFormData.cliente} readOnly className="w-full border-b border-gray-400 bg-gray-50 px-2 py-1 outline-none" />
                </div>
                <div className="col-span-2 flex gap-4">
                  <div className="w-1/2">
                    <label className="font-semibold text-gray-700 block mb-1">FECHA:</label>
                    <input type="date" value={checklistFormData.fecha} readOnly className="w-full border-b border-gray-400 bg-gray-50 px-2 py-1 outline-none" />
                  </div>
                </div>

                <div className="col-span-2">
                  <label className="font-semibold text-gray-700 block mb-1">Orden Produccion:</label>
                  <input type="text" value={checklistFormData.ordenProduccion} readOnly className="w-full border-b border-gray-400 bg-gray-50 px-2 py-1 outline-none" />
                </div>
                <div className="col-span-2 flex gap-4">
                  <div className="w-full">
                    <label className="font-semibold text-gray-700 block mb-1">Lote:</label>
                    <input type="text" value={checklistFormData.lote} onChange={(e) => setChecklistFormData({...checklistFormData, lote: e.target.value})} className="w-full border-b border-gray-400 px-2 py-1 outline-none focus:border-blue-500" />
                  </div>
                </div>

                <div className="col-span-2">
                  <label className="font-semibold text-gray-700 block mb-1">Equipo:</label>
                  <input type="text" value={checklistFormData.equipo} readOnly className="w-full border-b border-gray-400 bg-gray-50 px-2 py-1 outline-none" />
                </div>
                <div className="col-span-2 flex items-end">
                  <label className="font-semibold text-gray-700 mr-2 whitespace-nowrap">VELOCIDAD:</label>
                  <input type="text" value={checklistFormData.velocidad} onChange={(e) => setChecklistFormData({...checklistFormData, velocidad: e.target.value})} className="flex-1 border-b border-gray-400 px-2 py-1 outline-none focus:border-blue-500" />
                </div>
              </div>

              <div className="grid grid-cols-[300px_1fr] gap-4 mb-6">
                <div className="bg-gray-200 p-4 space-y-4 rounded font-medium">
                  <div className="flex justify-between items-center border-b border-gray-400 pb-1">
                    <span>TEMPERATURA TUNEL:</span>
                    <input type="text" value={checklistFormData.tempTunel} onChange={(e) => setChecklistFormData({...checklistFormData, tempTunel: e.target.value})} className="w-24 px-1 py-0.5 bg-white border border-gray-300 rounded text-sm font-normal" />
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-400 pb-1">
                    <span>TEMPERATURA TAMBOR:</span>
                    <input type="text" value={checklistFormData.tempTambor} onChange={(e) => setChecklistFormData({...checklistFormData, tempTambor: e.target.value})} className="w-24 px-1 py-0.5 bg-white border border-gray-300 rounded text-sm font-normal" />
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-400 pb-1">
                    <span>TEMPERATURA TINTERO:</span>
                    <input type="text" value={checklistFormData.tempTintero} onChange={(e) => setChecklistFormData({...checklistFormData, tempTintero: e.target.value})} className="w-24 px-1 py-0.5 bg-white border border-gray-300 rounded text-sm font-normal" />
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-400 pb-1">
                    <span>TENSION DE EMBOBINADOR:</span>
                    <input type="text" value={checklistFormData.tensionEmbobinador} onChange={(e) => setChecklistFormData({...checklistFormData, tensionEmbobinador: e.target.value})} className="w-24 px-1 py-0.5 bg-white border border-gray-300 rounded text-sm font-normal" />
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-400 pb-1">
                    <span>TENSION DE DESBOBINADOR:</span>
                    <input type="text" value={checklistFormData.tensionDesbobinador} onChange={(e) => setChecklistFormData({...checklistFormData, tensionDesbobinador: e.target.value})} className="w-24 px-1 py-0.5 bg-white border border-gray-300 rounded text-sm font-normal" />
                  </div>
                </div>
                <div></div>
              </div>

              <div className="overflow-x-auto mb-8">
                <table className="w-full border-collapse border border-gray-400">
                  <thead>
                    <tr>
                      <th className="border border-gray-400 p-2 text-left bg-gray-50 w-48">
                        <span className="text-xs text-gray-500 font-normal">← → para reordenar</span>
                      </th>
                      {[0,1,2,3,4,5,6,7].map(cIdx => (
                        <th key={cIdx} className="border border-gray-400 p-1 font-semibold text-center w-24 bg-blue-50">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-xs font-bold text-blue-800">CUERPO {cIdx + 1}</span>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => moveCuerpo(cIdx, 'left')}
                                disabled={cIdx === 0}
                                className="px-1.5 py-0.5 rounded text-xs font-bold bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                                title="Mover izquierda"
                              >←</button>
                              <button
                                type="button"
                                onClick={() => moveCuerpo(cIdx, 'right')}
                                disabled={cIdx === 7}
                                className="px-1.5 py-0.5 rounded text-xs font-bold bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                                title="Mover derecha"
                              >→</button>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {label: 'ANILOX:', key: 'anilox'},
                      {label: 'CODIGO DE ANILOX:', key: 'codigoAnilox'},
                      {label: 'BCM:', key: 'bcm'},
                      {label: 'COLOR:', key: 'color'},
                      {label: 'STICKIBACK:', key: 'stickiback'},
                      {label: 'VISCOSIDAD:', key: 'viscosidad'}
                    ].map((row, rIdx) => (
                      <tr key={rIdx}>
                        <td className="border border-gray-400 p-2 font-semibold bg-gray-50 text-xs">{row.label}</td>
                        {[0,1,2,3,4,5,6,7].map(cIdx => (
                          <td key={cIdx} className="border border-gray-400 p-0">
                            <input 
                              type="text" 
                              value={checklistFormData.cuerpos?.[cIdx]?.[row.key] || ''} 
                              onChange={(e) => {
                                const newCuerpos = [...(checklistFormData.cuerpos || [])];
                                if (!newCuerpos[cIdx]) newCuerpos[cIdx] = {};
                                newCuerpos[cIdx][row.key] = e.target.value;
                                setChecklistFormData({...checklistFormData, cuerpos: newCuerpos});
                              }}
                              className="w-full h-full p-2 outline-none focus:bg-blue-50 text-center"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                    {/* Approval checkbox row */}
                    <tr className="bg-amber-50">
                      <td className="border border-gray-400 p-2 font-bold bg-amber-100 text-xs text-amber-900 uppercase">APROBADO:</td>
                      {[0,1,2,3,4,5,6,7].map(cIdx => (
                        <td key={cIdx} className="border border-gray-400 p-2 text-center">
                          <label className="inline-flex flex-col items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checklistFormData.cuerpos?.[cIdx]?.aprobado || false}
                              onChange={(e) => {
                                const newCuerpos = [...(checklistFormData.cuerpos || [])];
                                if (!newCuerpos[cIdx]) newCuerpos[cIdx] = {};
                                newCuerpos[cIdx] = { ...newCuerpos[cIdx], aprobado: e.target.checked };
                                setChecklistFormData({...checklistFormData, cuerpos: newCuerpos});
                              }}
                              className="w-5 h-5 accent-green-600 cursor-pointer"
                            />
                            <span className={`text-xs font-semibold ${checklistFormData.cuerpos?.[cIdx]?.aprobado ? 'text-green-700' : 'text-gray-400'}`}>
                              {checklistFormData.cuerpos?.[cIdx]?.aprobado ? 'OK' : '—'}
                            </span>
                          </label>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* ROT-202: Plantillas Correctas — single overall confirmation,
                  not per-cuerpo, stored alongside every other checklist field
                  inside checklistData (jsonb) — no schema/migration needed. */}
              <div className="mb-4">
                <label className="inline-flex items-center gap-2 cursor-pointer bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                  <input
                    type="checkbox"
                    checked={checklistFormData.plantillasCorrectas || false}
                    onChange={(e) => setChecklistFormData({...checklistFormData, plantillasCorrectas: e.target.checked})}
                    className="w-5 h-5 accent-green-600 cursor-pointer"
                  />
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-900">Plantillas Correctas</span>
                </label>
              </div>

              {/* Personal y Registro de Tiempos */}
              <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-gray-50">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-3 border-b border-gray-300 pb-1">
                  Registro de Personal y Tiempos
                </h3>

                {/* Fechas y horas */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">FECHA / HORA DEL CAMBIO:</label>
                    <input
                      type="datetime-local"
                      value={checklistFormData.fechaHoraCambio || ''}
                      onChange={(e) => setChecklistFormData({...checklistFormData, fechaHoraCambio: e.target.value})}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">FECHA / HORA DE ENTONACIÓN:</label>
                    <input
                      type="datetime-local"
                      value={checklistFormData.fechaHoraEntonacion || ''}
                      onChange={(e) => setChecklistFormData({...checklistFormData, fechaHoraEntonacion: e.target.value})}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">FECHA / HORA DE ARRANQUE:</label>
                    <input
                      type="datetime-local"
                      value={checklistFormData.fechaHoraArranque || ''}
                      onChange={(e) => setChecklistFormData({...checklistFormData, fechaHoraArranque: e.target.value})}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Firmas de personal */}
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 text-left">OPERADOR DE ARRANQUE:</label>
                    <SearchableSelect
                      options={catalogEmployees.map(emp => ({
                        value: emp.name,
                        label: `${emp.numEmpleado ? emp.numEmpleado + ' - ' : ''}${emp.name}${emp.puesto ? ' (' + emp.puesto + ')' : ''}`,
                        searchText: `${emp.numEmpleado ?? ''} ${emp.name}`,
                      }))}
                      value={checklistFormData.operadorArranque || checklistFormData.operador || ''}
                      onChange={(val) => setChecklistFormData({...checklistFormData, operadorArranque: val, operador: val})}
                      placeholder="-- Busca por número o nombre --"
                    />
                    <div className="mt-1 border-b-2 border-gray-700 pb-1 text-sm text-center text-gray-800 min-h-[20px]">
                      {checklistFormData.operadorArranque || <span className="text-gray-400 italic text-xs">Sin seleccionar</span>}
                    </div>
                  </div>
                  <div className="text-center">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 text-left">SUPERVISOR DE PRODUCCIÓN:</label>
                    <SearchableSelect
                      options={catalogEmployees.map(emp => ({
                        value: emp.name,
                        label: `${emp.numEmpleado ? emp.numEmpleado + ' - ' : ''}${emp.name}${emp.puesto ? ' (' + emp.puesto + ')' : ''}`,
                        searchText: `${emp.numEmpleado ?? ''} ${emp.name}`,
                      }))}
                      value={checklistFormData.supervisorProduccion || checklistFormData.supervisor || ''}
                      onChange={(val) => setChecklistFormData({...checklistFormData, supervisorProduccion: val, supervisor: val})}
                      placeholder="-- Busca por número o nombre --"
                    />
                    <div className="mt-1 border-b-2 border-gray-700 pb-1 text-sm text-center text-gray-800 min-h-[20px]">
                      {checklistFormData.supervisorProduccion || <span className="text-gray-400 italic text-xs">Sin seleccionar</span>}
                    </div>
                  </div>
                  <div className="text-center">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 text-left">ASEGURAMIENTO DE CALIDAD:</label>
                    <SearchableSelect
                      options={catalogEmployees.map(emp => ({
                        value: emp.name,
                        label: `${emp.numEmpleado ? emp.numEmpleado + ' - ' : ''}${emp.name}${emp.puesto ? ' (' + emp.puesto + ')' : ''}`,
                        searchText: `${emp.numEmpleado ?? ''} ${emp.name}`,
                      }))}
                      value={checklistFormData.aseguramientoCalidad || ''}
                      onChange={(val) => setChecklistFormData({...checklistFormData, aseguramientoCalidad: val})}
                      placeholder="-- Busca por número o nombre --"
                    />
                    <div className="mt-1 border-b-2 border-gray-700 pb-1 text-sm text-center text-gray-800 min-h-[20px]">
                      {checklistFormData.aseguramientoCalidad || <span className="text-gray-400 italic text-xs">Sin seleccionar</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t flex justify-end gap-2 bg-gray-50">
              <Button variant="outline" onClick={() => { setShowChecklistModal(false); setIsEditingChecklist(false); setChecklistHistoryBanner(null); }}>Cancelar</Button>
              <Button onClick={saveChecklist} className="bg-blue-600 text-white hover:bg-blue-700">
                {isEditingChecklist ? 'Actualizar Checklist' : 'Guardar y Continuar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
